import * as THREE from 'three';
import { anyDeeds, deedsBoard, fightLines } from '../campaign/deeds.ts';
import { ARTIFICER, CELLARS, DRAGON, DUNGEON, INCURSION, SKILLS, lookOf, glyphTurns, STRUCTURES, WORKSHOP, type SkillId, type StructureType, INCURSION_TIERS, PLUNDER, MAZE, TICK_HZ, UNSLEEPING, UNSLEEPING_IDS, UNSLEEPING_OF, type UnsleepingId } from '@somnhold/content';
import {
  cellAtWorld,
  cellHex,
  cellId,
  hexLine,
  hexToWorld as hexToWorldFixed,
  heroWalkable,
  plunderLate,
  isBuildableTerrain,
  kitOf,
  isRevealed,
  targetable,
  stepBattle,
  structureAtCell,
  terrainAt,
  worldToHex,
  type BattleEvent,
  type BattleState,
  type Command,
  type Hero,
  type TickInputs,
  rankedSkill,
  levelSkill,
  castBlocked,
  wellBurned,
  wellStoneDone,
  fairSaved,
  laneIn,
  laneLit,
} from '@somnhold/sim';
import { BattleView, Post, simToWorld, TableCamera, worldToSim } from '@somnhold/render';
import { music } from '../audio/music.ts';
import { audio, type Sfx } from '../audio/sfx.ts';
import { t } from '../i18n.ts';
import { isTouch, settings } from '../settings.ts';
import { localSeason, viewSeason } from '../season.ts';
import { clear, h, setFill, setStyle, setText, viewH, viewW } from '../ui/dom.ts';
import { ICONS } from '../ui/icons.ts';
import { musicToggle } from '../ui/music-toggle.ts';
import { AimMarks } from './aim-marks.ts';
import { climbLine, DungeonHud, neckLine } from './dungeon-hud.ts';
import { carryAction, ExpeditionHud } from './expedition-hud.ts';
import { plunderCarryAction, plunderLeaveReady, PlunderWays } from './plunder-hud.ts';
import { RiftCue } from './rift-cue.ts';
import { LastFoes } from './last-foes.ts';
import { RescueWay } from './rescue-way.ts';
import { FurrowsHud } from './furrows-hud.ts';
import { reportError } from '../errors.ts';
import { ChatBox, type ChatChannel } from './chat.ts';
import { GroupChannel } from './group-chat.ts';
import { NameTags } from './name-tags.ts';
import { Signals } from './signals.ts';
import { PathTags } from './path-tags.ts';
import { CLASS_ICON, PartyFrames } from './party-frames.ts';
import { skillFacts } from '../campaign/skill-tree.ts';
import { showResTip } from '../ui/restip.ts';
import { Coach, type CoachView, type PinTarget } from './coach.ts';
import type { LiveLink } from './live.ts';
import { Loop } from './loop.ts';
import { PerfMeter } from './perf-meter.ts';
import { MovePredictor } from './predict.ts';

/** Sound for each nightmare's death and each hero skill (recorded takes; see audio/sfx.ts). */
const DIE_SFX: Record<string, Sfx> = {
  goblin: 'die-goblin',
  wolf: 'die-wolf',
  skeleton: 'die-skeleton',
  wraith: 'die-wraith',
  gargoyle: 'die-gargoyle',
  slime: 'die-slime',
  stalker: 'die-stalker',
};
const SKILL_SFX: Record<string, Sfx> = {
  // Ser Corvin, the Unsleeping (docs/ANTIHEROES.md).
  corvinArrive: 'boss-shell',
  corvinChallenge: 'shout',
  corvinCharge: 'shieldBash',
  corvinNight: 'curse',
  corvinBroken: 'shatter',
  corvinPhase: 'horn',
  corvinFlee: 'curse',
  corvinHeld: 'shieldBash',
  // Kaela of the Moss.
  kaelaArrive: 'boss-shell',
  kaelaThorns: 'briarLash',
  kaelaPack: 'clanRoar',
  kaelaMoss: 'deepRoot',
  kaelaForest: 'forestAwakening',
  kaelaBear: 'horn',
  kaelaBroken: 'shatter',
  kaelaPhase: 'horn',
  kaelaFlee: 'curse',
  kaelaHeld: 'shieldBash',
  // Prior Lucan.
  lucanArrive: 'boss-witch',
  lucanWhip: 'briarLash',
  lucanRaise: 'plague',
  lucanChapel: 'sanctuary',
  lucanBroken: 'shatter',
  lucanPhase: 'bell',
  lucanFlee: 'curse',
  lucanHeld: 'shieldBash',
  // Ser Edda.
  eddaArrive: 'boss-shell',
  eddaJudgement: 'dawnJudgment',
  eddaAegis: 'holyRampart',
  eddaPillar: 'faithRay',
  eddaOath: 'oath',
  eddaBroken: 'shatter',
  eddaPhase: 'bell',
  eddaFlee: 'curse',
  eddaHeld: 'shieldBash',
  // Vela, the Archer without arrows.
  velaArrow: 'piercingShot',
  velaLeap: 'disengage',
  velaRain: 'arrowRain',
  // Ansel, the Ash Scholar.
  anselEmber: 'fire',
  anselWall: 'flamethrower',
  anselLibrary: 'fireRain',
  // Bruni, the Hollow Forger.
  bruniRivet: 'titanArrow',
  bruniMortar: 'turret',
  bruniForge: 'ingenuity',
  // Ragna, the Horn.
  ragnaLeap: 'charge',
  ragnaHorn: 'horn',
  ragnaSally: 'clanRoar',
  shieldBash: 'shieldBash',
  challenge: 'shout',
  oath: 'oath',
  turret: 'build',
  gearGrenade: 'net',
  automaton: 'ingenuity',
  fireball: 'skill',
  frostWall: 'frost',
  storm: 'storm',
  multishot: 'arrow',
  net: 'net',
  arrowRain: 'arrowRain',
  mend: 'mend',
  sanctuary: 'sanctuary',
  dawn: 'dawn',
  // The learned skills (docs/PODERES.md §5).
  decoy: 'decoy',
  frostOrb: 'frostOrb',
  hawk: 'hawk',
  guardTotem: 'totem',
  spinHammer: 'hammer',
  clanRoar: 'clanRoar',
  repairDrone: 'drone',
  wasps: 'wasps',
  lowBlow: 'lowBlow',
  chainLightning: 'chainLightning',
  poisonArrow: 'poisonArrow',
  faithRay: 'faithRay',
  throwingAxe: 'throwingAxe',
  blindingLight: 'blindingLight',
  spikeMine: 'spikeMine',
  charge: 'charge',
  deepRoot: 'deepRoot',
  guardBanner: 'guardBanner',
  blizzard: 'blizzard',
  titanArrow: 'titanArrow',
  dawnJudgment: 'dawnJudgment',
  holyRampart: 'holyRampart',
  fieldMortar: 'fieldMortar',
  plague: 'plague',
  grapple: 'grapple',
  tuneUp: 'tuneUp',
  frostLance: 'frostLance',
  piercingShot: 'piercingShot',
  renew: 'renew',
  brutalBlow: 'brutalBlow',
  holySeal: 'holySeal',
  briarLash: 'briarLash',
  defyShout: 'defyShout',
  teslaCoil: 'teslaCoil',
  blink: 'blink',
  snare: 'snare',
  sharedPrayer: 'sharedPrayer',
  bloodletting: 'bloodletting',
  intervene: 'intervene',
  thornWall: 'thornWall',
  lastStand: 'lastStand',
  clockHound: 'clockHound',
  fireRain: 'fireRain',
  eagleEye: 'eagleEye',
  intercession: 'intercession',
  ancestralFury: 'ancestralFury',
  avatarOfLight: 'avatarOfLight',
  forestAwakening: 'forestAwakening',
  shieldSlam: 'shieldSlam',
  shieldWall: 'shieldWall',
  shieldEcho: 'shieldEcho',
  flamethrower: 'flamethrower',
  ironBarricade: 'ironBarricade',
  fortifyAll: 'fortifyAll',
  acidDart: 'acidDart',
  shadowChains: 'shadowChains',
  dreamRift: 'dreamRift',
  disengage: 'disengage',
  signalFlare: 'signalFlare',
  hiddenShot: 'hiddenShot',
  piercingLight: 'piercingLight',
  cleanse: 'cleanse',
  choir: 'choir',
  kick: 'kick',
  terrify: 'terrify',
  duel: 'duel',
  atone: 'atone',
  blessWeapon: 'blessWeapon',
  sacrifice: 'sacrifice',
  sporeCloud: 'sporeCloud',
  wolfForm: 'wolfForm',
  rootQuake: 'rootQuake',
};

/** The Unsleeping an event's name belongs to (`<id><Name>`), if any. */
const unOf = (name: string): UnsleepingId | undefined => UNSLEEPING_IDS.find((id) => name.startsWith(id));

/**
 * The unique pieces' forms (docs/UNICAS.md §3), by the burst or line they make: existing takes by family (PODERES §5),
 * each one its own pair so two forms never sound alike. Once per event, never per frame.
 */
const FORM_SFX: Record<string, [Sfx, number][]> = {
  bashWave: [['shieldSlam', 0.7]],
  riftStun: [['rock', 0.35]],
  gateShut: [['shieldBash', 0.8], ['bell', 0.45]],
  anchor: [['rootQuake', 0.6]],
  bloodSwirl: [['bloodletting', 0.55]],
  toll: [['bell', 0.35]],
  gasparGuard: [['intervene', 0.8], ['choir', 0.35]],
  oldRoot: [['deepRoot', 0.35]],
  rootSnap: [['snare', 0.55]],
  lastTurn: [['lastStand', 0.9]],
  unfallen: [['ancestralFury', 0.5]],
  counterFrost: [['frost', 0.4]],
  hug: [['grapple', 0.45]],
  forgeRebuild: [['build', 0.8], ['fire', 0.4]],
  lightWard: [['holySeal', 0.4]],
  lance: [['piercingShot', 0.55]],
  lostTrail: [['hiddenShot', 0.6]],
  coldBlood: [['frost', 0.35], ['shout', 0.3]],
  boarCharge: [['charge', 0.8]],
  steelEcho: [['shieldEcho', 0.8]],
  steelOath: [['holyRampart', 0.6]],
  oldBear: [['forestAwakening', 0.6]],
  medal: [['oath', 0.7], ['bell', 0.35]],
  guardingDawn: [['sharedPrayer', 0.45]],
  closedSky: [['arrowRain', 0.6]],
  garden: [['renew', 0.5]],
  doubleNet: [['net', 0.6]],
  flasks: [['tuneUp', 0.5], ['shatter', 0.3]],
  spikes: [['arrow', 0.35]],
  tangle: [['briarLash', 0.35]],
  vengeance: [['boom', 0.8], ['shieldSlam', 0.5]],
  discharge: [['teslaCoil', 0.55]],
  lucerna: [['faithRay', 0.7]],
  relay: [['intervene', 0.45]],
  stormJump: [['chainLightning', 0.45]],
  leapFlame: [['avatarOfLight', 0.45]],
  heavySmite: [['shieldBash', 0.7], ['holyRampart', 0.4]],
  secondDawn: [['sharedPrayer', 0.4], ['bell', 0.25]],
  crescendo: [['rootQuake', 0.45], ['charge', 0.4]],
  hunger: [['bloodletting', 0.5], ['renew', 0.35]],
  endless: [['charge', 0.5], ['hiddenShot', 0.3]],
  highLamp: [['faithRay', 0.45], ['holySeal', 0.3]],
};

/** What every Unsleeping's shared moments sound like (his own skills are in SKILL_SFX). */
const UN_SFX: Record<string, Sfx> = { Arrive: 'boss-shell', Broken: 'shatter', Phase: 'horn', Flee: 'curse', Held: 'shieldBash' };
const unSfx = (skill: string): Sfx | undefined => {
  const un = unOf(skill);
  return un ? UN_SFX[skill.slice(un.length)] : undefined;
};

export interface BattleScreenOpts {
  state: BattleState;
  me: string;
  renderer: THREE.WebGLRenderer;
  /** Practice sandbox: build cards + start button. */
  sandbox: boolean;
  allowPause: boolean;
  title: string;
  subtitle?: string;
  buildable?: StructureType[];
  thumbs?: Record<string, string>;
  onEnd: (s: BattleState, log: { tick: number; cmds: Command[] }[]) => void;
  onExit: () => void;
  /** Replay mode: feed these inputs for this player; no local control. */
  replay?: { pid: string | null; inputs: { tick: number; cmds: Command[] }[]; bundles?: { tick: number; inputs: TickInputs }[] };
  /** Live lockstep: the network drives the simulation. */
  net?: LiveLink;
  /** Model at the center of the board (incursions show their node). */
  centerpiece?: string;
  /** Incursion: the node and what a full extraction brings, main resource first. */
  extraction?: { kind: string; loot: [string, number][] };
  /** Live: when the wave starts (server clock) and how to name the other players. */
  liveInfo?: { due: number; now: () => number; names: Record<string, string>; /** The hero's preparations, to change what it carries while gathering (docs/PODERES.md §3). */ carry?: HTMLElement; /** Crypt: who already took today's chest. */ claimed?: () => string[]; /** Fewest heroes to set out (a chapter played again: its owner may go alone). */ minParty?: number; /** The keep's chat, joined to the room's; `poll` brings its new lines. */ keep?: { channel: ChatChannel; poll(): Promise<void> }; /** The rest of the group while gathering (who is around and where), refreshed by `poll`. */ allies?: { el: HTMLElement; render(): void; poll(): Promise<unknown>; /** Leave out who is already in this room. */ skip?(pids: string[]): void } };
  /** Practice: the guide is available; `true` starts it right away. */
  coach?: boolean;
  /**
   * The bastion from scratch (docs/CONSTRUCCION.md): walls are drawn by dragging, an eraser takes things
   * down, and `hud` goes over the board (the Tide that comes next).
   */
  land?: { hud?: HTMLElement };
}

interface DmgNum {
  el: HTMLElement;
  pos: THREE.Vector3;
  t: number;
}

/** The camera starts to pan or turn only after the pointer moves this far (px). */
const DRAG_START = 14;
/** Holding the pointer still this long (ms) on the board starts steering: the hero follows it until it lets go. */
const STEER_HOLD = 260;
/** Press closer than this (px) to your own hero and a drag steers it right away instead of moving the camera. */
const STEER_GRAB = 56;
/** How long the end's title holds the screen alone (ms) before the result card or the way out. */
const END_HOLD_MS = 1600;
/** How long the end waits when an Unsleeping fading into smoke ended the fight (his card and smoke seen first). */
const FLED_HOLD_MS = 2400;
/** The night of the bell won at dawn: the last stroke, the silence and the camera going up, before the victory. */
const FINALE_HOLD_MS = 8500;
/** A fight on this device's own clock waits for its nightmares to be baked and linked at most this long. */
const HOLD_MAX_MS = 15000;
/** The first drawing waits for the scene's shaders at most this long (FIRST_DRAW). */
const FIRST_MAX_MS = 4000;

/** Released fast and close to where it started: a click, even if the camera already slid a little. */
const quickClick = (p: { sx: number; sy: number; t: number }, e: PointerEvent): boolean =>
  performance.now() - p.t < 300 && Math.hypot(e.clientX - p.sx, e.clientY - p.sy) < 30;

const fmtTime = (ticks: number): string => {
  const s = Math.max(0, Math.floor(ticks / 12));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

/** One battle on screen: render loop, input → commands, HUD. */
export class BattleScreen {
  readonly el: HTMLElement;
  private canvas: HTMLCanvasElement;
  private view: BattleView;
  /** Live: my own walking drawn ahead of the room's ticks (see MovePredictor). */
  private predict = new MovePredictor();
  private cam: TableCamera;
  private post: Post;
  private meter: PerfMeter;
  /** For the device report's hitches: enemy kinds seen so far, and when a new kind or a new room last showed up. */
  private kindsSeen = new Set<string>();
  private lastNew = { at: 0, what: '' };
  private lastRoom = -1;
  private loop: Loop;
  private queue: Command[] = [];
  private log: { tick: number; cmds: Command[] }[] = [];
  private hud: HTMLElement;
  private keepBar!: HTMLElement;
  private extractBar!: HTMLElement;
  /** The line that says there are clues to pick up (gone once they all are). */
  private clueRule: HTMLElement | null = null;
  private betRule: HTMLElement | null = null;
  private turretBtn: HTMLElement | null = null;
  /** An expedition: pick up what is at hand, or put down what you carry. */
  private carryBtn: HTMLButtonElement | null = null;
  /** A plunder: head home with what is on the cart (everyone still standing by it), and its arrows. */
  private leaveBtn: HTMLButtonElement | null = null;
  private plunderWays: PlunderWays | null = null;
  private lastFoes: LastFoes | null = null;
  private riftCue: RiftCue | null = null;
  private rescueWay: RescueWay | null = null;
  private rescueRule: HTMLElement | null = null;
  /** El Párpado 1 up top: when the titan shakes next (or the dust is up: stand still). */
  private neckRule: HTMLElement | null = null;
  /** El Párpado 2 on the climb: the bell's ropes, who is pulling, how far up. */
  private climbRule: HTMLElement | null = null;
  private lashEl: HTMLElement | null = null;
  /** The hunt: how much of the Old Uro is left, and the traps; the trap button. */
  private huntEl: HTMLElement | null = null;
  /** The dragon of the Burn: its six scale marks, the glass's cold, and the first lit lane already told. */
  private dragonScales: HTMLElement[] = [];
  private dragonCold: HTMLElement | null = null;
  private dragonColdBar: HTMLElement | null = null;
  private laneTold = false;
  /** The dragon: when each scale last closed (to flash it), the count broken last frame, the opening lines put away. */
  private dragonClosed: number[] = [0, 0, 0, 0, 0, 0];
  private dragonBroken = -1;
  /** The fall's banner (dragon red, above the board, never over its middle). */
  private dragonBanner: HTMLElement | null = null;
  private dragonBannerTimer = 0;
  private dragonIntroDone = false;
  /** Arrows at the screen's edge toward a lit harpoon out of view, each with its seconds to the crossing. */
  private laneArrows: { el: HTMLElement; txt: HTMLElement }[] = [];
  /** El Párpado 3: whose copy is out (yours: you hit it twice as hard), the outing's faces, the time left. */
  private copiesEl: HTMLElement | null = null;
  private copiesLbl: HTMLElement | null = null;
  private copiesSeen = '';
  private trapBtn: HTMLButtonElement | null = null;
  private strikeBtn: HTMLButtonElement | null = null;
  /** The night of the bell at dusk: the Hollow Shell's life shows in the keep's card. */
  private nightBoss = false;
  /** The five strikes: the last count-in played (ticks before the ring closes), so each sounds once. */
  private anvilBeat = -1;
  /** A plunder: how long until the golem leaves on its own. */
  private golemEl = h('b.pl-golem');
  /** The artificer's overload button, and whether it is picking a turret to blow up. */
  private overloadBtn: HTMLElement | null = null;
  private overloading = false;
  private placingTurret = false;
  private keepText!: HTMLElement;
  private waveText!: HTMLElement;
  private waveTrack!: HTMLElement;
  private waveNum!: HTMLElement;
  private waveLabel!: HTMLElement;
  private waveFill!: HTMLElement;
  private waveTotal = 0;
  private waveIco: HTMLElement | null = null;
  /** My hero's knock-out: a countdown right under the wave tracker. */
  private downEl!: HTMLElement;
  private downNum!: HTMLElement;
  private downFill!: HTMLElement;
  private downMax = 0;
  private waveKills = (s: { enemies: { hp: number }[]; wave: { next: number } }): number => s.wave.next - s.enemies.filter((e) => e.hp > 0).length;
  private resEls: Record<string, HTMLElement> = {};
  private skillEls: HTMLElement[] = [];
  private skillsEl: HTMLElement | null = null;
  private heroBar!: HTMLElement;
  private heroText!: HTMLElement;
  private cards: { type: StructureType; el: HTMLElement }[] = [];
  private startWrap: HTMLElement | null = null;
  private banner!: HTMLElement;
  private toastEl!: HTMLElement;
  private ultEl!: HTMLElement;
  /** «Preparing the fight»: shown while a held clock waits longer than a blink (see Loop.held). */
  private prepEl!: HTMLElement;
  private heldSince = 0;
  private toastTimer = 0;
  private dmgLayer: HTMLElement;
  private dmg: DmgNum[] = [];
  private pointers = new Map<number, { x: number; y: number; sx: number; sy: number; t: number; button: number }>();
  private gesture: { mode: 'none' | 'pan' | 'rotate' | 'pinch' | 'steer' | 'aim' | 'paint'; dist: number; angle: number } = { mode: 'none', dist: 0, angle: 0 };
  /** Terraced land: the eraser is on (a tap or a drag takes down what stands there). */
  private erasing = false;
  /** Terraced land: the last cell a drag painted. */
  private painted = -1;
  /** Steering: the hold timer, and where (screen) and when the last move went out. */
  private steerTimer = 0;
  private steerSent = { x: 0, y: 0, t: 0 };
  private nameTags = new NameTags(() => this.o.me, () => this.o.liveInfo?.names ?? {});
  private pathTags = new PathTags();
  private aiming: 0 | 1 | 2 | -1 = -1;
  /** PC: a skill key held down; the skill shows where it goes and casts when the key is let go. */
  private held: 0 | 1 | 2 | -1 = -1;
  private marks: AimMarks;
  private building: StructureType | null = null;
  private dragCard: { type: StructureType; pointerId: number; ghost: HTMLElement } | null = null;
  private hoverCell: { q: number; r: number } | null = null;
  private mouse = { x: 0, y: 0, inside: false };
  private ended = false;
  private perf: HTMLElement | null = null;
  private ray = new THREE.Raycaster();
  private plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  private disposers: (() => void)[] = [];
  private lastHudTick = -1;
  private coach: Coach | null = null;

  constructor(private o: BattleScreenOpts) {
    this.el = h('div.game');
    this.canvas = o.renderer.domElement;
    this.canvas.classList.add('gl');
    this.el.append(this.canvas);
    this.view = new BattleView(o.state.map, o.state.seed, { quality: settings.quality, me: o.me, ...(o.centerpiece ? { centerpiece: o.centerpiece } : {}), ...(o.state.dungeon?.hallWins ? { exitHall: true } : {}), ...(o.net && o.state.dungeon ? { torches: Math.max(DUNGEON.maxHeroes, MAZE.maxHeroes, CELLARS.maxHeroes) } : {}), ...viewSeason(o.state.season ?? (o.state.mode === 'practice' ? localSeason(Date.now()).season : undefined)) });
    this.marks = new AimMarks(this.view.scene);
    if (o.net) this.view.heroLead = (hero, x, y) => this.predict.lead(this.o.state, hero, x, y, performance.now());
    // The fog omen: a grey mist closes in on the clearing.
    if (o.state.rules?.omen === 'fog') this.mist = this.view.scene.fog = o.state.mode === 'dungeon' ? new THREE.Fog('#14161c', 7, 22) : new THREE.Fog('#5b6270', 9, 30);
    // Every omen also tints the edges of the screen, never covering the board.
    if (o.state.rules?.omen) this.el.append(h('div.omen-veil', { 'data-omen': o.state.rules.omen }));
    this.cam = new TableCamera(viewW() / viewH());
    this.cam.bound = ((o.state.map.radius + 1) * 1.2) / 1.4;
    this.frameSector(o.state.heroes.find((x) => x.pid === o.me)?.post ?? 0);
    if (o.state.rules?.dragon) {
      // The dragon's clearing: from farther out and over its middle, so the harpoons on the rim are in the picture.
      this.cam.maxDist = 42;
      this.cam.focus(new THREE.Vector3(0, 0, 0), true);
      this.cam.setDistance(window.innerWidth <= 640 ? 40 : 32, true);
      this.laneArrows = o.state.rules.dragon.harpoons.map(() => {
        const txt = h('b');
        const el = h('div.lane-arrow', { hidden: true, 'data-test': 'lane-arrow' }, h('i'), txt);
        this.el.append(el);
        return { el, txt };
      });
    }
    if (o.land) {
      this.el.classList.add('land');
      // The keep stands on the crest: look at it from a little farther out, and pan the whole land.
      this.cam.bound = o.state.map.radius * 1.2;
      this.cam.maxDist = 46;
      this.cam.focus(new THREE.Vector3(0, 1.4, 0), true);
      // A phone is narrow: start farther out so the crest and its slopes fit across it.
      this.cam.setDistance(window.innerWidth <= 640 ? 40 : 24, true);
      if (o.land.hud) this.el.append(o.land.hud);
    }
    this.post = new Post(o.renderer, this.view.scene, this.cam.camera, settings.tiltShift);
    const map = o.state.map;
    const tier = o.state.rules?.tier ? String(o.state.rules.tier) : '';
    const mode = map.kind === 'dungeon' ? (map.look ?? (o.state.dungeon?.exp ? 'cellars' : `crypt${tier}`)) : map.kind === 'incursion' ? `incursion${tier}` : 'wave';
    this.meter = new PerfMeter(mode, !!o.net, o.renderer);
    this.meter.context = () => {
      const st = this.o.state;
      const fresh = performance.now() - this.lastNew.at < 2000 ? ` ${this.lastNew.what}` : '';
      return `t${st.tick} e${st.enemies.length}${st.dungeon ? ` r${st.dungeon.room}` : ''}${fresh}`;
    };
    if (o.net) {
      const net = o.net;
      this.meter.net = () => ({ seen: net.seen, resyncs: net.client.resyncs });
      this.meter.netBusy = () => {
        const ms = net.busy;
        net.busy = 0;
        return ms;
      };
    }
    this.dmgLayer = h('div', { style: 'position:absolute;inset:0;pointer-events:none;overflow:hidden' });
    this.hud = this.buildHud();
    this.el.append(this.dmgLayer, this.hud);
    if (o.state.plunder) {
      this.plunderWays = new PlunderWays(() => this.o.state, o.me);
      this.hud.append(this.plunderWays.el);
    }
    if (o.state.rules?.clearToFinish) {
      this.lastFoes = new LastFoes(() => this.o.state, o.me);
      this.hud.append(this.lastFoes.el);
    }
    if (o.state.rules?.rifts) {
      this.riftCue = new RiftCue(() => this.o.state);
      this.hud.append(this.riftCue.el);
    }
    if (o.state.rules?.rescue || o.state.rules?.lanterns || o.state.rules?.herd || o.state.rules?.collapse || o.state.rules?.trap || o.state.rules?.blast || o.state.rules?.bells) {
      this.rescueWay = new RescueWay(() => this.o.state, o.me);
      this.hud.append(this.rescueWay.el);
    }
    this.ensureParty();
    if (o.state.mode === 'dungeon') {
      // The crypt has its own HUD (party, rooms, the Warden) and a camera that walks with you; an expedition, its own.
      const toast = (m: string, ms?: number): void => this.toast(m, ms);
      this.dg = o.state.dungeon?.exp ? new ExpeditionHud(() => this.o.state, o.me, o.liveInfo?.names ?? {}, toast) : o.state.dungeon?.maze ? new FurrowsHud(() => this.o.state, o.me, o.liveInfo?.names ?? {}, toast, (c) => this.send(c)) : new DungeonHud(() => this.o.state, o.me, o.liveInfo?.names ?? {}, toast, (c) => this.send(c));
      this.hud.classList.add('dungeon');
      if (o.state.dungeon?.exp) this.hud.classList.add('expedition');
      if (o.state.dungeon?.maze) this.hud.classList.add('expedition', 'furrows');
      this.hud.append(this.dg.el);
      if (this.allySlot() >= 0) this.dg.party.onPick = (id) => this.healAlly(id);
      const meHero = this.me();
      this.cam.bound = (o.state.map.radius + 2) * 1.2;
      if (meHero) this.cam.focus(simToWorld(meHero.x, meHero.y), true);
      this.cam.setDistance(viewW() < viewH() ? 17 : 13, true);
      // Look down into the halls more steeply, over the walls.
      this.cam.rotate(0, 0.22);
    }
    if (o.coach !== undefined) {
      this.coach = new Coach(this.hud, () => this.me(), () => this.startWave(), (v) => this.coachView(v));
      this.hud.append(this.coach.el, this.coach.pin);
      if (o.coach) window.setTimeout(() => this.coach?.begin(), 400);
    }
    this.loop = new Loop(
      () => this.tick(),
      (dt) => this.frame(dt),
    );
    this.bindInput();
    this.resize();
    // Every frame calls interpolate: a tick takes in its events and the board is drawn once per frame.
    this.view.deferDraw = true;
    this.view.sync(o.state);
    // On this device's own clock, the fight starts once its nightmares are baked (see BattleView.bakeAhead).
    this.loop.held = !o.net;
    if (o.net) {
      // Lockstep: every tick the server closes arrives here, already simulated, and plays at once (holding
      // ticks back to pace them evenly added up to two ticks of lag on top of the round trip). What moves
      // glides from the last tick to this one starting now.
      this.loop.driven = true;
      o.net.client.onTick = (st) => {
        this.o.state = st;
        try {
          this.afterTick(st);
        } catch (e) {
          // Whatever broke on screen, the fight goes on and its end still shows.
          reportError(e, 'tick');
          this.checkEnd(st);
        }
        this.loop.beat();
      };
      // A reconnect (or a rebuilt lobby, or a desync) replaced the state: take it as it comes, ended included.
      o.net.client.onReset = (st) => {
        this.o.state = st;
        this.view.sync(st);
        // A rebuilt lobby may bring another preparation: the buttons follow what the hero carries now.
        const me = this.me();
        if (me) this.fitSkills(me);
        this.checkEnd(st);
      };
      o.net.onStatus = (status) => {
        if (status === 'lost') this.toast(t('live.reconnecting'));
        if (status === 'open') this.toastEl.classList.remove('show');
        if (status === 'cancelled' && this.lobbyEl && !this.o.state.started) this.lobbyCancelled();
        else if (status === 'cancelled' || status === 'full' || status === 'replaced' || status === 'gone' || status === 'refused') this.linkStopped(status);
      };
      // Chat of the room and of the keep as one (the group's talk follows you in): inside the waiting room's
      // card while gathering, folded into a button once the fight starts.
      const keep = o.liveInfo?.keep;
      this.chat = new ChatBox(keep ? new GroupChannel(o.net.client, keep.channel) : o.net.client, o.me, o.liveInfo?.names ?? {}, (line) => !this.lobbyEl && this.nameTags.say(line.pid, line.text, this.o.state.heroes));
      if (o.state.started) this.chat.dock(this.hud);
      if (!o.replay) this.buildSignals(o.net);
      // Who else is around and what they're up to, to know whether to wait for them (gone once it starts),
      // and what the group says in the keep (all along): both come with one presence poll.
      const allies = o.liveInfo?.allies;
      const poll = keep?.poll ?? allies?.poll;
      if (poll && (keep || !o.state.started)) {
        const refresh = (): void => {
          if (this.o.state.started) this.dropAllies();
          if (this.o.state.started && !keep) return window.clearInterval(this.socialTimer);
          void poll().then(() => {
            if (!this.o.state.started) allies?.render();
          });
        };
        refresh();
        this.socialTimer = window.setInterval(refresh, 5000);
      }
    }
    if (new URLSearchParams(location.search).has('perf')) {
      this.perf = h('div.perf');
      this.el.append(this.perf);
    }
  }

  start(): void {
    this.loop.start();
    void music.play('marea');
    // Incursions: say once what the turret button does, it is the only incursion-only control. Together,
    // once the party sets out (in the waiting room it covered the card's buttons on a phone).
    if (this.o.extraction && !this.o.replay) {
      if (this.o.state.started) this.turretHint();
      else this.turretHintDue = true;
    }
    // A plunder: say once, up front, what it asks and how it ends.
    if (this.o.state.plunder && !this.o.replay) window.setTimeout(() => this.toast(this.o.state.rules?.place === 'camp' ? t('side.campIntro', { n: PLUNDER.winLoad }) : t('plunder.intro', { n: PLUNDER.fullLoad }), 7000), 600);
    // Touch: once ever, say that holding a power explains it.
    if (isTouch() && !this.o.replay) {
      try {
        if (!localStorage.getItem('somnhold.holdTip')) {
          localStorage.setItem('somnhold.holdTip', '1');
          window.setTimeout(() => this.toast(t('hud.holdSkill'), 4500), this.o.extraction ? 11000 : 4000);
        }
      } catch {
        /* no storage: skip the tip */
      }
    }
    // Test hook (Playwright smoke test reads the battle state).
    (window as unknown as { __somnhold?: unknown }).__somnhold = { screen: this, state: this.o.state, loop: this.loop };
  }

  private turretHintDue = false;

  private turretHint(): void {
    this.turretHintDue = false;
    window.setTimeout(() => this.toast(t('inc.turretHint', { n: this.turretMax(), s: Math.round((this.o.state.rules?.turrets?.ticks ?? 0) / 12) }), 6000), 4200); // after the opening banner
  }

  /** Test/dev helper: screen position (CSS px) of a hex cell center. */
  cellToScreen(q: number, r: number): { x: number; y: number } {
    const c = hexToWorldFixed(q, r);
    const v = simToWorld(c.x, c.y).project(this.cam.camera);
    return { x: (v.x * 0.5 + 0.5) * viewW(), y: (-v.y * 0.5 + 0.5) * viewH() };
  }

  /** Test/dev helper: screen position (CSS px) of a sim point on the ground. */
  simToScreen(x: number, y: number): { x: number; y: number } {
    const v = simToWorld(x, y).project(this.cam.camera);
    return { x: (v.x * 0.5 + 0.5) * viewW(), y: (-v.y * 0.5 + 0.5) * viewH() };
  }

  /** Test/dev helper: point the camera at a world position. */
  focusSim(x: number, y: number, dist?: number): void {
    this.cam.focus(simToWorld(x, y));
    if (dist) this.cam.setDistance(dist);
  }

  /** Looks from the keep toward a sector's gate, with the approach path at the top of the screen. */
  frameSector(index: number): void {
    const sec = this.o.state.map.sectors[index];
    if (!sec) return;
    const g = hexToWorldFixed(sec.gate.q, sec.gate.r);
    const gw = simToWorld(g.x, g.y);
    const portrait = viewW() < viewH();
    this.cam.focus(gw.clone().multiplyScalar(portrait ? 0.7 : 0.78), true);
    this.cam.setYaw(Math.atan2(-gw.x, -gw.z), true);
    this.cam.setDistance(portrait ? 25 : 19, true);
  }

  get state(): BattleState {
    return this.o.state;
  }

  /**
   * El Párpado 3: the bar is the copy out now (its name, or «your copy»: you hit it twice as hard); under it, the
   * outing's faces in the order they come out (the felled struck out, the one out marked) and the time left.
   */
  /** The fall's banner: big, dragon red, over the top of the board (its middle stays clear), a few seconds. */
  private dragonBannerShow(text: string): void {
    if (!this.dragonBanner) {
      this.dragonBanner = h('div.dragon-banner', { 'data-test': 'dragon-banner', hidden: true });
      this.el.append(this.dragonBanner);
    }
    const b = this.dragonBanner;
    setText(b, text);
    b.hidden = false;
    b.classList.remove('in');
    void b.offsetWidth;
    b.classList.add('in');
    window.clearTimeout(this.dragonBannerTimer);
    this.dragonBannerTimer = window.setTimeout(() => {
      b.hidden = true;
      b.classList.remove('in');
    }, 2600);
  }

  /** The dragon's marks under its life: six scales (whole, broken, never closing) and the glass's cold. */
  private dragonHud(): HTMLElement {
    this.dragonScales = Array.from({ length: 6 }, () => h('i'));
    this.dragonColdBar = h('i', { style: 'transform:scaleX(0)' });
    this.dragonCold = h('div.dragon-cold', { title: t('saga.burn.hud.coldHint') }, h('span.label', {}, t('saga.burn.hud.cold')), h('div.bar', {}, this.dragonColdBar));
    return h('div.dragon-hud', { 'data-test': 'dragon-hud' }, h('div.dragon-scales', { title: t('saga.burn.hud.scalesHint'), 'aria-label': t('saga.burn.hud.scalesHint') }, ...this.dragonScales), this.dragonCold);
  }

  /**
   * The dragon this frame: its scales (whole, brittle, broken, never closing, closing in fire) and cold under its life;
   * on the track, in one line, what it is doing and its clock; the opening lines put away after a few seconds; arrows
   * at the screen's edge toward a lit harpoon out of view.
   */
  private updateDragon(s: BattleState): void {
    const st = s.dragon;
    const r = s.rules?.dragon;
    if (!r) return;
    const broken = st?.broken ?? r.fixed;
    const now = performance.now();
    // One closed (fewer broken than last frame): the one that knits flashes orange a moment.
    if (this.dragonBroken >= 0 && broken < this.dragonBroken) for (let i = broken; i < this.dragonBroken; i++) this.dragonClosed[i] = now;
    // One broke: its mark bursts (once, by restarting its animation).
    if (this.dragonBroken >= 0 && broken > this.dragonBroken)
      for (let i = this.dragonBroken; i < broken; i++) {
        const el = this.dragonScales[i];
        if (!el) continue;
        el.classList.remove('pop');
        void el.offsetWidth;
        el.classList.add('pop');
      }
    this.dragonBroken = broken;
    const cold = st ? Math.min(1, st.cold / Math.max(1, r.ask)) : 0;
    const brittle = !!st && (st.phase === 'down' || cold >= 1);
    this.dragonScales.forEach((el, i) => {
      const k = i < broken ? (i < r.fixed ? 'fixed' : 'broken') : now - (this.dragonClosed[i] ?? 0) < 1200 ? 'closing' : brittle ? 'brittle' : 'whole';
      if (el.dataset.k !== k) {
        el.dataset.k = k;
        el.classList.remove('whole', 'brittle', 'broken', 'fixed', 'closing');
        el.classList.add(k);
      }
    });
    if (this.dragonColdBar) setFill(this.dragonColdBar, brittle ? 1 : cold);
    this.dragonCold?.classList.toggle('brittle', brittle);
    const sec = (ticks: number): number => Math.max(0, Math.ceil(ticks / 12));
    const phase = st?.phase ?? 'sky';
    setText(this.waveNum, '');
    setText(this.waveText, '');
    if (phase === 'fury' && st) {
      setText(this.waveLabel, t('saga.burn.hud.furyLine', { s: sec(Math.min(st.glassAt, st.furyAt + DRAGON.fury.max) - s.tick), m: fmtTime(Math.max(0, DRAGON.fury.max - (s.tick - st.furyAt))) }));
      setFill(this.waveFill, Math.min(1, (s.tick - st.furyAt) / DRAGON.fury.max));
    } else {
      setText(this.waveLabel, t('saga.burn.hud.line', { what: t(`saga.burn.hud.${phase}`), m: fmtTime(Math.max(0, DRAGON.clock - s.waveT)) }));
      setFill(this.waveFill, Math.min(1, s.waveT / DRAGON.clock));
    }
    this.waveTrack.classList.add('dragon');
    this.waveTrack.classList.remove('last');
    this.waveTrack.classList.toggle('hot', phase === 'fury' || phase === 'down');
    this.waveTrack.classList.toggle('done', s.result === 'won');
    // The harpoons' line and La Quemadura's edges are for the start: a few seconds, then out of the way.
    if (!this.dragonIntroDone && s.started && s.waveT > 12 * 10) {
      this.dragonIntroDone = true;
      for (const el of this.el.querySelectorAll<HTMLElement>('.dragon-intro')) el.hidden = true;
    }
    // The first lane that lights is told once (the rest is seen on the ground, or by its arrow).
    if (!this.laneTold && st?.phase === 'sky' && r.harpoons.some((_, i) => laneLit(s, i))) {
      this.laneTold = true;
      this.toast(t('saga.burn.hud.lane'), 2600);
    }
    const W = viewW();
    const H = viewH();
    r.harpoons.forEach((hp, i) => {
      const a = this.laneArrows[i];
      if (!a) return;
      const ticks = laneIn(s, i);
      const p = ticks < 0 ? null : this.simToScreen(hp.lx, hp.ly);
      // Kept clear of the HUD: below the top cards, above the skills.
      const m = 34;
      const top = W <= 640 ? 200 : 100;
      const bottom = W <= 640 ? 230 : 150;
      const inside = !!p && p.x > m && p.x < W - m && p.y > top && p.y < H - bottom;
      if (!p || inside) {
        if (!a.el.hidden) a.el.hidden = true;
        return;
      }
      if (a.el.hidden) a.el.hidden = false;
      // From the middle of the screen toward it, stopped at the edge.
      const cx = W / 2;
      const cy = (top + H - bottom) / 2;
      const dx = p.x - cx;
      const dy = p.y - cy;
      const k = Math.min((W / 2 - m) / Math.max(1, Math.abs(dx)), ((H - bottom - top) / 2) / Math.max(1, Math.abs(dy)));
      setStyle(a.el, 'transform', `translate(${(cx + dx * k).toFixed(0)}px, ${(cy + dy * k).toFixed(0)}px)`);
      setStyle(a.el, '--a', `${Math.atan2(dy, dx).toFixed(3)}rad`);
      setText(a.txt, ticks === 0 ? t('saga.burn.hud.now') : t('saga.burn.hud.in', { s: sec(ticks) }));
    });
  }

  private updateCopies(s: BattleState): void {
    const r = s.rules?.copies;
    if (!r) return;
    const st = s.copies;
    const out = st?.id ? s.enemies.find((e) => e.id === st.id && e.hp > 0) : undefined;
    const face = out ? r.faces[(st?.next ?? 1) - 1] : undefined;
    const mine = !!face && face.pid === this.o.me;
    if (this.copiesLbl) setText(this.copiesLbl, face ? (mine ? t('side.copies.yours') : t('side.copies.of', { name: face.name })) : st?.done ? t('side.copies.allDown') : t('side.copies.none'));
    this.copiesLbl?.classList.toggle('mine', mine);
    setFill(this.extractBar, out ? Math.max(0, out.hp) / Math.max(1, out.maxHp) : 0);
    const el = this.copiesEl;
    if (!el) return;
    const fallen = st?.fallen ?? [];
    const time = t('side.huntTime', { t: fmtTime(Math.max(0, r.out - s.waveT)) });
    // Rebuilt only when what it says changes (the time is the last span's text).
    const key = `${fallen.length}:${face?.pid ?? ''}`;
    if (key !== this.copiesSeen) {
      this.copiesSeen = key;
      clear(el);
      for (const f of r.faces) {
        const cls = fallen.includes(f.pid) ? '.down' : f === face ? '.out' : '';
        el.append(h(`span.cf${cls}${f.pid === this.o.me ? '.me' : ''}`, {}, f.pid === this.o.me ? t('side.copies.you') : f.name));
      }
      el.append(h('span.ct', {}, time));
    } else setText(el.lastElementChild as HTMLElement, time);
  }

  private me(): Hero | undefined {
    return this.o.state.heroes.find((x) => x.pid === this.o.me);
  }

  // ---------- simulation ----------

  private tick(): void {
    // Live: the server's ticks play as they arrive (see the constructor).
    if (this.o.net) return;
    const s = this.o.state;
    if (s.result !== 'running') return;
    let cmds = this.queue;
    this.queue = [];
    if (this.o.replay) {
      cmds = [];
      const pid = this.o.replay.pid;
      for (const e of this.o.replay.inputs) if (e.tick === s.tick + 1 && pid) cmds.push(...e.cmds);
    }
    const who = this.o.replay ? this.o.replay.pid ?? '' : this.o.me;
    let inputs: TickInputs = cmds.length > 0 ? { [who]: cmds } : {};
    if (this.o.replay?.bundles) inputs = this.o.replay.bundles.find((b) => b.tick === s.tick + 1)?.inputs ?? {};
    if (cmds.length > 0 && !this.o.replay) this.log.push({ tick: s.tick + 1, cmds });
    stepBattle(s, inputs);
    this.afterTick(s);
  }

  /** Unsleeping that faded into smoke (their 'die' is no fall). */
  private readonly unFled = new Set<number>();
  private unCard: HTMLElement | null = null;
  /** The tick an Unsleeping faded into smoke (the end, if his going ended the fight, waits to be seen). */
  private fledTick = -1;

  /**
   * The Unsleeping's moments (docs/ANTIHEROES.md): his name large as he comes in (and, back again, his line for the
   * hero he hunts), the Night of the Gate named as he channels it, «cut!» when the party breaks it, and the end.
   */
  private unsleepingMoments(s: BattleState): void {
    for (const e of s._events ?? []) {
      // The Prestacaras (Caras prestadas 4): it wears friends who aren't here; say whose face it takes, and when it slips.
      // El Párpado 3: the group's own copies wear their faces openly (no Prestacaras' toast); say who comes out and falls.
      if (e.t === 'face' && s.rules?.copies) continue;
      if (e.t === 'copy') {
        const f = s.rules?.copies?.faces.find((x) => x.pid === e.pid);
        const mine = e.pid === this.o.me;
        if (f) this.toast(t(mine ? `side.copies.${e.kind}Mine` : `side.copies.${e.kind}`, { name: f.name }), e.kind === 'out' ? 3600 : 3000);
        continue;
      }
      if (e.t === 'face') {
        const f = s.enemies.find((x) => x.id === e.id)?.mask?.faces.find((x) => x.pid === e.pid);
        this.toast(f ? t('side.face.now', { name: f.name }) : t('side.face.cal'), 4200);
        continue;
      }
      if (e.t === 'unmask' && e.face === 'facetaker') {
        this.toast(t('side.face.slip'), 3200);
        continue;
      }
      // The lash: the creak before it goes, and the big one.
      if (e.t === 'lash') {
        if (e.kind === 'warn') this.toast(t(s.lash && s.rules?.lash && s.lash.n === s.rules.lash.total - 1 ? 'side.lash.big' : 'side.lash.warn'), 3000);
        continue;
      }
      // The hunt: the Uro pawing at me (stand still!), losing me, a trap going off.
      if (e.t === 'hunt') {
        const mine = this.me();
        if (e.kind === 'wind' && mine && s.hunt?.tgt === mine.id) this.toast(t('side.hunt.seen'), 2600);
        else if (e.kind === 'snort' && mine && s.hunt?.tgt === mine.id) this.toast(t('side.hunt.lost'), 2400);
        else if (e.kind === 'caught') this.toast(t('side.hunt.caught'), 3000);
        continue;
      }
      // The clan's call: Ragna hears it and kneels.
      if (e.t === 'call') {
        this.toast(t('side.call.heard'), 3200);
        continue;
      }
      // Not a scratch: a nightmare hit Hilde's wall, and the bet is gone.
      if (e.t === 'scratch') {
        this.toast(t('bet.unscathed.hit'), 3200);
        continue;
      }
      // The runaway carts: a barricade on the rails, a cart off them, the bridge up or down, the train over the edge.
      if (e.t === 'rails') {
        const left = (s.rules?.rails?.carts ?? 0) - (s.rails?.derailed ?? 0);
        this.toast(t(`side.rails.${e.kind}`, { n: left }), e.kind === 'wall' ? 1800 : 3200);
        continue;
      }
      // One alone: a nightmare got past Brann's stakes.
      if (e.t === 'alone') {
        const of = s.rules?.alone?.max ?? 0;
        this.toast(t('bet.alone.hit', { n: e.n, of }), 2600);
        if (this.betRule) setText(this.betRule, t('bet.alone.hud', { n: e.n, of }));
        continue;
      }
      // The five strikes: on the beat, off it (yours), the fifth.
      if (e.t === 'anvil') {
        const mine = s.heroes.find((x) => x.pid === this.o.me);
        if (e.kind === 'miss' && mine && mine.x === e.x && mine.y === e.y) this.toast(t('side.anvil.miss'), 1600);
        else if (e.kind !== 'miss') this.toast(t(`side.anvil.${e.kind}`), 2600);
        continue;
      }
      // The bell at midnight: on the beat (a deep, cracked bell), off it (yours), the calm full.
      if (e.t === 'toll') {
        const mine = s.heroes.find((x) => x.pid === this.o.me);
        if (e.kind === 'hit' || e.kind === 'calm') audio.play('greatBell', e.kind === 'calm' ? 1 : 0.8);
        if (e.kind === 'miss' && mine && mine.x === e.x && mine.y === e.y) this.toast(t('side.toll.miss'), 1600);
        else if (e.kind === 'hit') this.toast(t('side.toll.hit'), 1300);
        else if (e.kind === 'calm') this.toast(t('side.toll.calmFull'), 3000);
        continue;
      }
      // The night of the bell: an ally came in.
      if (e.t === 'ally') {
        this.toast(t(`side.night.ally.${e.npc}`), 4200);
        audio.play('horn', 0.7);
        continue;
      }
      // The Maw: dark and lit again, braziers out and lit, its bite on you, felled.
      if (e.t === 'maw') {
        const mine = s.heroes.find((x) => x.pid === this.o.me);
        if (e.kind === 'bite') {
          if (mine && Math.abs(mine.x - e.x) + Math.abs(mine.y - e.y) < 300) this.toast(t('side.maw.bite'), 1600);
        } else if (e.kind !== 'rise') this.toast(t(`side.maw.${e.kind}`), 2400);
        continue;
      }
      // The fin: a lane marked, the wave, dawn.
      if (e.t === 'fin') {
        // La Orilla 5: the dragon's fire down the lane in its first fight; the fin over the dragon in its last.
        // The fin on the dragon says so itself (crushed, or it moved): that is the news, not the wave.
        if (s.rules?.sunken?.boss && e.kind === 'sweep' && (s._events ?? []).some((x) => x.t === 'sunken' && (x.kind === 'crush' || x.kind === 'shrug'))) continue;
        const k = s.rules?.fin?.breath && e.kind !== 'dawn' ? `${e.kind}Fire` : s.rules?.sunken?.boss && e.kind === 'warn' ? 'warnBig' : e.kind;
        this.toast(t(`side.fin.${k}`), e.kind === 'warn' ? 2400 : 2000);
        continue;
      }
      // El Párpado 1: the dust before the titan shakes (stand still!), and what it did to me; a fold squeezing me back.
      if (e.t === 'neck') {
        const mine = this.me();
        const near = mine && Math.abs(mine.x - e.x) + Math.abs(mine.y - e.y) < 300;
        if (e.kind === 'dust') this.toast(t(this.o.state.rules?.climb ? 'side.climb.dust' : 'side.neck.dust'), 2400);
        else if ((e.kind === 'fall' || e.kind === 'squeeze') && near) this.toast(t(`side.neck.${e.kind}Me`), 2200);
        continue;
      }
      // El Párpado 2: a rope cut, a slip, the bell braced or rolling away; hanging it, the bell up.
      if (e.t === 'hoist') {
        if (e.kind === 'cut' || e.kind === 'slip' || e.kind === 'brace' || e.kind === 'roll' || e.kind === 'up') this.toast(t(`side.hoist.${e.kind}`), e.kind === 'roll' || e.kind === 'up' ? 3000 : 2200);
        continue;
      }
      // La Orilla 5: the Drowned Dragon's moments (its breath is drawn on the ground, not said), and the heart.
      if (e.t === 'sunken') {
        if (e.kind !== 'draw' && e.kind !== 'breath') this.toast(t(`side.sunken.${e.kind}`), e.kind === 'crush' || e.kind === 'felled' || e.kind === 'rise' ? 3000 : 2000);
        continue;
      }
      // The low water: the water turning, the net loose, home, dawn or lost; a Drowned's grip (yours, or a friend's).
      if (e.t === 'tide') {
        if (e.kind === 'grab' || e.kind === 'free') {
          const mine = s.heroes.find((x) => x.pid === this.o.me);
          const near = mine && Math.abs(mine.x - e.x) + Math.abs(mine.y - e.y) < 400;
          if (near) this.toast(t(`side.tide.${e.kind}Me`), e.kind === 'grab' ? 2600 : 1400);
          else if (e.kind === 'grab') this.toast(t('side.tide.grab'), 2200);
        } else if (e.kind !== 'lift') this.toast(t(`side.tide.${e.kind}`), 3000);
        continue;
      }
      // Fair day: Fenn's cart taken, a ware dropped, put back or lost, the fair closing.
      if (e.t === 'fair') {
        const cart = e.stall === s.rules?.fair?.cart;
        if (e.kind === 'taken') {
          if (cart) this.toast(t('side.fair.cart'), 2600);
        } else this.toast(t(`side.fair.${e.kind}`), e.kind === 'back' ? 1400 : 2400);
        continue;
      }
      // The Drinker: its round (swollen is the moment to hit it), the burst and the water coming.
      if (e.t === 'drinker') {
        this.toast(t(`${s.rules?.drinker?.up ? 'side.flight' : 'side.drinker'}.${e.kind}`), e.kind === 'wave' || e.kind === 'burst' ? 3200 : 2200);
        continue;
      }
      // The ink in the water: a house burned, the mill's stone out, the well sealed.
      if (e.t === 'well') {
        if (e.kind !== 'hit') this.toast(t(`side.well.${e.kind}`), 3000);
        continue;
      }
      // What comes up: a tower marked, kept or turned; Dorn's automatons up the rails.
      if (e.t === 'rising') {
        this.toast(t(`side.rising.${e.kind}`), e.kind === 'saved' ? 1400 : 2800);
        continue;
      }
      // The alarm bells: a lever down, all of them (the pull itself shows on the track and the lever's studs).
      if (e.t === 'bells') {
        if (e.kind === 'down' || e.kind === 'all') this.toast(t(`side.bells.${e.kind}`), 3000);
        continue;
      }
      // The drive: a stampede, wolves after a stray, a boar lost (a bolt is seen, not told).
      // The blasting: a barrel down, a crack sealed (or not yet), one set off in the hands, the beacon.
      if (e.t === 'blast') {
        if (e.kind === 'ready' || e.kind === 'crack' || e.kind === 'seal' || e.kind === 'burst' || e.kind === 'beacon') this.toast(t(`side.blast.${e.kind}`), 2600);
        continue;
      }
      // The dragon of the Burn: brought down, raging, up again; a harpoon in it (a scale breaking is seen and heard, not told).
      if (e.t === 'boom' && (e.kind === 'dragonDown' || e.kind === 'dragonFury' || e.kind === 'dragonRise')) {
        if (e.kind === 'dragonDown') this.dragonBannerShow(t('saga.burn.hud.down'));
        else this.toast(t(`saga.burn.hud.${e.kind}`), e.kind === 'dragonFury' ? 3600 : 2600);
        if (e.kind === 'dragonDown') {
          audio.play('boom', 1);
          window.setTimeout(() => audio.play('crumble', 1), 380);
        } else audio.play(e.kind === 'dragonFury' ? 'horn' : 'crumble', 0.8);
        continue;
      }
      // A scale breaks: the glass rings (the best blow of the fight); fire closing one hisses.
      if (e.t === 'boom' && (e.kind === 'dragonScale' || e.kind === 'dragonClose')) {
        audio.play(e.kind === 'dragonScale' ? 'shatter' : 'fire', e.kind === 'dragonScale' ? 1 : 0.6);
        continue;
      }
      if (e.t === 'shot' && e.kind === 'harpoon') {
        // The one that brings it down says so with its banner instead.
        if (!(s._events ?? []).some((x) => x.t === 'boom' && x.kind === 'dragonDown')) this.toast(t('saga.burn.hud.harpoon'), 2000);
        audio.play('bolt', 1);
        continue;
      }
      // The old trap: wound and ready, and how each shot went.
      if (e.t === 'trap') {
        if (e.kind === 'armed' || e.kind === 'hit' || e.kind === 'miss') this.toast(t(`side.trap.${e.kind}`), 2600);
        continue;
      }
      // The collapse: a scout lost to the break, or to the nightmares.
      if (e.t === 'collapse') {
        if (e.kind === 'lost') this.toast(t('side.collapse.lost'), 3200);
        continue;
      }
      if (e.t === 'herd') {
        if (e.kind !== 'panic' && e.kind !== 'pen') this.toast(t(`side.herd.${e.kind}`), 3200);
        continue;
      }
      const un = unOf(e.t === 'boom' ? e.kind : e.t === 'skill' ? e.skill : '');
      if (!un) continue;
      // Its borrowed kit's moments are not the Unsleeping's own (no names, no lines).
      if (e.t === 'skill' && s.enemies.find((x) => x.id === e.hero)?.mask) continue;
      if (e.t === 'boom' && e.kind === `${un}Vanish`) {
        const who = s.enemies.find((x) => x.un && x.hp <= 0) ?? s.enemies.find((x) => x.kind === un);
        if (who) this.unFled.add(who.id);
        this.fledTick = s.tick;
        this.unsleepingCard(t(`enemy.${un}`), t(`unsleeping.${un}.fled`), undefined, false, 'fled');
        continue;
      }
      if (e.t !== 'skill') continue;
      if (e.skill === `${un}Arrive`) {
        void music.play('boss');
        const en = s.enemies.find((x) => x.id === e.hero);
        const hunted = en?.un?.hunt ? s.heroes.find((x) => x.pid === en.un?.hunt) : undefined;
        const back = t(`unsleeping.${un}.back`);
        this.unsleepingCard(t(`enemy.${un}`), t(`unsleeping.${un}.title`), en?.un && en.un.returns > 0 ? (hunted ? `${hunted.name}: «${back}»` : `«${back}»`) : undefined);
      } else if (e.skill === `${un}${UNSLEEPING_OF[un].ult}`) this.unsleepingCard(t(`unsleeping.${un}.ult`), '', undefined, false, 'night');
      else if (e.skill === `${un}Broken`) this.unsleepingCard(t('unsleeping.broken'), '', undefined, false, 'broken');
      // Fading into smoke: the fight looks still while it lasts, so say so, with the time left to hold him.
      else if (e.skill === `${un}Flee`) this.unsleepingCard(t('unsleeping.flee', { name: t(`enemy.${un}`) }), t('unsleeping.fleeHint'), undefined, false, 'flee');
      else if (e.skill === `${un}Held`) this.unsleepingCard(t('unsleeping.held'), '', undefined, false, 'broken');
    }
    for (const hl of s.stats.highlights) {
      const un = hl.kind === 'bossSlain' && hl.tick === s.tick ? UNSLEEPING_IDS.find((x) => x === hl.what) : undefined;
      if (!un) continue;
      const who = (hl.pid && s.heroes.find((x) => x.pid === hl.pid)?.name) || t('moment.someone');
      this.unsleepingCard(t(`enemy.${un}`), t(`unsleeping.${un}.slain`, { who }), undefined, true);
    }
  }

  /** The Unsleeping's card: his name in the chronicle's hand under a broken seal of the Order, for a few seconds. */
  private unsleepingCard(name: string, title: string, line?: string, fallen = false, moment?: 'night' | 'broken' | 'flee' | 'fled'): void {
    this.unCard?.remove();
    const card = h(
      `div.unsleeping-card${fallen ? '.fallen' : ''}${moment ? `.moment.${moment}` : ''}`,
      { 'data-test': 'unsleeping-card' },
      h('div.seal', { 'aria-hidden': 'true' }),
      h('div.name', {}, name),
      title ? h('div.title', {}, title) : '',
      line ? h('div.line', {}, line) : '',
      moment === 'flee' ? h('div.flee-bar', { style: `animation-duration: ${UNSLEEPING.fleeTicks / TICK_HZ}s` }, h('i')) : '',
    );
    this.el.querySelector('.hud')?.append(card);
    this.unCard = card;
    // The path tags at the gates sit right where the card comes up: they step aside while it shows.
    this.el.classList.add('un-carded');
    const ms = fallen || moment === 'fled' ? 4200 : moment === 'flee' ? (UNSLEEPING.fleeTicks * 1000) / TICK_HZ : moment ? 1600 : 3000;
    window.setTimeout(() => card.classList.add('out'), ms);
    window.setTimeout(() => {
      card.remove();
      if (this.unCard === card) this.el.classList.remove('un-carded');
    }, ms + 1000);
  }

  private ambushTold = false;
  private glassTold = 0;
  private shineTold = 0;

  /** Obsidiana (level III, and the deep crypt's last floor): say what the glass and the colossus want, the first times. */
  private obsidianHints(s: BattleState): void {
    if (s.rules?.tier !== 3) return;
    for (const e of s._events ?? []) {
      if (e.t === 'skill' && e.skill === 'colossusShine' && this.shineTold < 3) {
        this.shineTold++;
        this.toast(t('colossus.shine'), 2600);
      }
    }
    const me = s.heroes.find((x) => x.pid === this.o.me);
    if (!me || this.glassTold >= 2) return;
    const crack = s.zones.find((z) => z.kind === 'glass' && (z.warn ?? 0) > 0 && Math.abs(z.x - me.x) + Math.abs(z.y - me.y) < 900);
    if (crack && crack.warn === INCURSION_TIERS[3].glassWarn - 1) {
      this.glassTold++;
      this.toast(t('obsidian.move'), 2200);
    }
  }

  private boltTold = 0;
  private starTold = false;

  /** The storm and the falling stars: say what they want the first times, and who took a star. */
  private omenHints(s: BattleState): void {
    const me = s.heroes.find((x) => x.pid === this.o.me);
    if (s.rules?.bolts && me && this.boltTold < 2) {
      const near = s.zones.find((z) => z.kind === 'bolt' && z.warn === (s.rules?.bolts?.warn ?? 0) - 1 && Math.abs(z.x - me.x) + Math.abs(z.y - me.y) < 1600);
      if (near) {
        this.boltTold++;
        this.toast(t('omen.boltHint'), 2400);
      }
    }
    if (!s.rules?.stars) return;
    for (const e of s._events ?? []) {
      if (e.t === 'boom' && e.kind === 'starfall' && !this.starTold) {
        this.starTold = true;
        this.toast(t('omen.starHint'), 2600);
      }
      if (e.t === 'skill' && e.skill === 'starPick') {
        const h = s.heroes.find((x) => x.id === e.hero);
        if (h) this.toast(h.pid === this.o.me ? t('omen.starPickMe') : t('omen.starPick', { who: h.name }), 2200);
      }
    }
  }

  /** The wave's battle plan (docs/TACTICAS.md): never said beforehand, only a horn at each of the general's moves. */
  private planHints(s: BattleState): void {
    if (!s.plan) return;
    for (const e of s._events ?? []) {
      if (e.t !== 'plan') continue;
      audio.play('horn', 0.7);
      // A strike says what it was (the feint, the pincer, the rush); the siege's moves say what they send.
      this.toast(t(e.move === 'strike' ? `plan.${s.plan.id}.strike` : `plan.move.${e.move}`), 3200);
    }
  }

  private afterTick(s: BattleState): void {
    this.view.sync(s);
    this.obsidianHints(s);
    this.unsleepingMoments(s);
    // A side saga's clue picked up: what it says, for everyone in the fight.
    for (const e of s._events ?? []) {
      if (e.t !== 'clue') continue;
      this.toast(`${t('side.clueFound')} ${t(`clue.${e.id}`)}`, 5200);
      if (this.clueRule && (s.clues?.found.length ?? 0) >= (s.rules?.clues?.length ?? 0)) this.clueRule.remove();
    }
    this.omenHints(s);
    this.planHints(s);
    // The forest's rule, said once, the moment the first ambush is about to come out of the trees.
    if (!this.ambushTold && s.rules?.twist === 'forest') {
      const sp = s.wave.spawns.slice(s.wave.next, s.wave.next + 30).find((x) => x.at);
      if (sp && sp.tick - s.waveT <= 24) {
        this.ambushTold = true;
        this.toast(t('inc.ambushNow'), 3500);
      }
    }
    this.coach?.update(s, this.o.coach !== undefined);
    this.sound(s._events ?? []);
    this.spawnNumbers(s._events ?? []);
    // Down the deep crypt's stairs: the camera cuts to the new floor while the screen is black.
    const jump = this.view.takeJump();
    if (jump) this.cam.focus(jump, true);
    const shake = this.view.takeShake();
    if (shake > 0) this.cam.addShake(shake);
    if (this.dg instanceof ExpeditionHud || this.dg instanceof FurrowsHud) this.dg.events(s);
    this.checkEnd(s);
  }

  private checkEnd(s: BattleState): void {
    if (s.result !== 'running' && !this.ended) {
      this.ended = true;
      // An Unsleeping fading into smoke was the last on the field: let his going be seen before the victory.
      // The night of the bell won at dawn (the end of all the sagas): its last stroke, silence, the camera going up.
      const finale = s.result === 'won' && this.view.bellFinale();
      const hold = finale ? FINALE_HOLD_MS : s.result === 'won' && this.fledTick === s.tick ? FLED_HOLD_MS : 0;
      this.endedAt = performance.now() + hold;
      if (finale) this.finale(hold);
      const show = (): void => {
        this.finaleEl?.remove();
        this.finaleEl = null;
        audio.play(s.result === 'won' ? 'victory' : 'defeat');
        this.showEnd(s.result === 'won', s.dungeon?.maze?.outcome, s);
      };
      if (hold > 0) window.setTimeout(show, hold);
      else show();
      // What follows (the result sent, the state fetched) starts now; its card waits on settle().
      this.o.onEnd(s, this.log);
    }
  }

  private endedAt = 0;
  private endEl: HTMLElement | null = null;
  private finaleEl: HTMLElement | null = null;
  /** Until when the camera keeps going up over the bastion (the night of the bell's finale). */
  private finaleUntil = 0;

  /** The night of the bell's finale: the HUD gone, the last stroke, the music out, three lines over the board. */
  private finale(hold: number): void {
    this.tooltip(null);
    this.toastEl.classList.remove('show');
    this.el.classList.add('ended');
    audio.play('greatBell', 1);
    void music.play('silent');
    this.finaleUntil = performance.now() + hold;
    this.cam.focus(new THREE.Vector3(0, 0, 0));
    this.finaleEl = h('div.bell-finale', { 'data-test': 'bell-finale' }, ...['l1', 'l2', 'l3'].map((k) => h('p', {}, t(`side.night.finale.${k}`))));
    this.el.append(this.finaleEl);
  }

  /**
   * The fight is over: say so at once, over the board, with the controls gone. Once the end has been
   * seen for a moment and the result is still on its way, a line says the battle is being written down,
   * so the wait never looks like a frozen game.
   */
  private showEnd(won: boolean, maze: '' | 'back' | 'torch' | 'centre' | undefined, s: BattleState): void {
    this.endedAt = performance.now();
    this.tooltip(null);
    this.toastEl.classList.remove('show');
    this.el.classList.add('ended');
    this.endEl = h(
      `div.battle-end.${won ? 'won' : 'lost'}`,
      { role: 'status', 'aria-live': 'assertive', 'data-test': 'battle-end' },
      // The Furrows end won three ways: only the centre is a victory; up the rope or out of torch, the run just ends with what it found.
      h('div.be-title', {}, won && (maze === 'back' || maze === 'torch') ? t(`maze.end.${maze}`) : won ? t('end.victory') : t('end.defeat')),
      h('div.be-rule'),
      won && (maze === 'back' || maze === 'torch') ? h('div.be-sub', {}, t(`maze.end.${maze}Sub`)) : null,
      // An Unsleeping that got away isn't beaten: say so, so the victory isn't read as his fall.
      s.stats.unsleeping?.outcome === 'fled' ? h('div.be-sub', { 'data-test': 'end-fled' }, t(`unsleeping.${s.stats.unsleeping.id}.fled`)) : null,
      h('div.be-wait', {}, h('span.be-dots', {}, h('i'), h('i'), h('i')), t('end.settling')),
    );
    // The fight's deeds (who struck, healed, held and felled the most): read at leisure, then on to what follows.
    const lines = fightLines(s, this.o.me, this.o.liveInfo?.names);
    if (anyDeeds(lines)) {
      this.endEl.classList.add('with-deeds');
      this.endEl.insertBefore(h('div.be-deeds', {}, h('div.be-deeds-head', {}, t('deeds.fightTitle')), deedsBoard(lines, { titles: true, test: 'fight-deeds' })), this.endEl.querySelector('.be-wait'));
    }
    this.el.append(this.endEl);
  }

  /** What comes after the fight (its card, or the way out), once the end has been on screen long enough to read. */
  settle(next: () => HTMLElement | void): void {
    // The end itself still held back (an Unsleeping's going being seen): settle once it shows.
    if (!this.endEl) {
      window.setTimeout(() => this.settle(next), Math.max(0, this.endedAt - performance.now()) + 20);
      return;
    }
    const wait = Math.max(0, END_HOLD_MS - (performance.now() - this.endedAt));
    const go = (): void => {
      const card = next();
      if (!card) return;
      this.endEl?.classList.add('done');
      this.el.append(card);
    };
    // With the deeds on screen the player moves on when done reading them; otherwise, as soon as the end was seen.
    const end = this.endEl;
    if (!end?.classList.contains('with-deeds')) {
      window.setTimeout(go, wait);
      return;
    }
    window.setTimeout(() => {
      const btn = h('button.btn.be-go', { 'data-test': 'deeds-go', onclick: () => { end.classList.add('gone'); go(); } }, t('deeds.go'));
      end.querySelector('.be-wait')?.replaceWith(btn);
      btn.focus({ preventScroll: true });
    }, wait);
  }

  /**
   * The room won't go on for this player (called off, full, opened again elsewhere, gone): say so plainly,
   * with the way back, instead of a frozen board.
   */
  private linkStopped(why: 'cancelled' | 'full' | 'replaced' | 'gone' | 'refused'): void {
    if (this.ended || this.el.querySelector('.link-stopped')) return;
    this.toastEl.classList.remove('show');
    this.el.append(
      h(
        'div.start-wrap.crypt-lobby.lobby.link-stopped',
        { 'data-test': 'link-stopped', role: 'alertdialog' },
        h('div.lobby-head', {}, h('div', {}, h('h3', {}, t(`live.stopped.${why}`)), h('p.lobby-sub', {}, t(`live.stopped.${why}Sub`)))),
        h('div.lobby-actions', {}, h('button.btn.small', { 'data-test': 'stopped-back', onclick: () => this.o.onExit() }, t('party.back'))),
      ),
    );
  }

  send(c: Command): void {
    if (this.o.replay) return;
    if (this.o.net) {
      this.o.net.client.input([c]);
      // My own walking starts on screen at once; any other order (an attack, a skill) waits for the room.
      const me = this.me();
      if (me) {
        const shown = this.view.positions.get(me.id);
        if (c.t === 'move' && shown) this.predict.moved(this.o.state, me, worldToSim(shown), c.x, c.y, performance.now());
        else if (c.t !== 'move') this.predict.cancel(me.x, me.y, performance.now());
      }
      return;
    }
    this.queue.push(c);
  }

  private sound(events: BattleEvent[]): void {
    for (const e of events) {
      switch (e.t) {
        case 'beam':
          // The forms' lines (the skills' own lines sound with their cast).
          for (const [n, v] of FORM_SFX[e.kind] ?? []) audio.play(n, v);
          break;
        case 'shot':
          if (e.kind === 'bolt') audio.play('bolt', 0.7);
          else if (e.kind === 'rock') audio.play('rock', 0.8);
          else if (e.kind === 'boulder') audio.play('rock', 1);
          else if (e.kind === 'arcane' || e.kind === 'chain') audio.play('arcane', 0.7);
          else if (e.kind === 'slash') audio.play('slash');
          else if (e.kind === 'curse') audio.play('curse');
          else if (e.kind === 'spikes' || e.kind === 'oil') audio.play('hit');
          else if (e.kind === 'arrow') audio.play('arrow', 0.6);
          else if (e.kind === 'bullet') audio.play('turret', 0.6);
          else if (e.kind === 'orb') audio.play('arcane', 0.5);
          break;
        case 'boom':
          if (FORM_SFX[e.kind]) for (const [n, v] of FORM_SFX[e.kind] as [Sfx, number][]) audio.play(n, v);
          else if (e.kind === 'slam' || e.kind === 'nova' || e.kind === 'ring') audio.play('boom', 1);
          else if (e.kind === 'rock') audio.play('boom', 0.6);
          else if (e.kind === 'boulder') audio.play('boom', 0.9);
          else if (e.kind === 'fireball') audio.play('fire');
          else if (e.kind.startsWith('overload') || e.kind.startsWith('grenade')) audio.play('boom', 0.8);
          else if (e.kind === 'spiderBlast') audio.play('boom', 0.45);
          else if (e.kind === 'pumpkin') {
            audio.play('boom', 0.7);
            audio.play('fire', 0.6);
          }
          else if (e.kind === 'sweep') audio.play('slash', 0.5);
          else if (e.kind === 'automaton') audio.play('build', 0.9);
          else if (e.kind === 'botBroken') audio.play('crumble', 0.6);
          // Reactions between damage types.
          else if (e.kind === 'shatter') audio.play('shatter', 0.9);
          else if (e.kind === 'combust') audio.play('combust', 0.9);
          else if (e.kind === 'mineBurst') audio.play('boom', 0.7);
          // The mortar's shells landing after the shot.
          else if (e.kind === 'mortar') audio.play('boom', 0.6);
          else if (e.kind === 'mortarHeavy') audio.play('boom', 1);
          else if (e.kind === 'snareShut') audio.play('snare', 0.7);
          // The Unsleeping: Corvin's Night landing (it breaks walls), anyone fading into smoke, their other blows.
          else if (e.kind === 'corvinNight') {
            audio.play('boom', 1);
            audio.play('crumble', 0.8);
          } else if (e.kind === 'kaelaForest') {
            audio.play('boom', 1);
            audio.play('thornWall', 0.8);
          } else if (e.kind === 'kaelaThorns') audio.play('thornWall', 0.6);
          else if (e.kind === 'kaelaRoots') audio.play('snare', 0.6);
          else if (e.kind === 'kaelaMoss') audio.play('deepRoot', 0.4);
          else if (e.kind === 'velaVolley') audio.play('arrow', 0.5);
          else if (e.kind === 'velaLand') audio.play('hit', 0.4);
          else if (e.kind === 'anselBurst' || e.kind === 'anselRing') audio.play('fire', 0.35);
          else if (e.kind === 'anselAshGone') audio.play('crumble', 0.3);
          else if (e.kind === 'anselColumn') audio.play('combust', 0.8);
          else if (unOf(e.kind)) audio.play(e.kind.endsWith('Vanish') ? 'curse' : 'boom', 0.8);
          break;
        case 'bell':
          audio.play('bell', 0.7);
          break;
        case 'die':
          // Each nightmare dies with its own sound; bosses and anything without one fall back to the generic.
          audio.play(DIE_SFX[e.kind] ?? 'die', 0.6);
          // An Unsleeping falling for good: his glass shatters and a bell rings clear.
          if (UNSLEEPING_IDS.some((x) => x === e.kind) && !this.unFled.has(e.id)) {
            audio.play('shatter', 1);
            audio.play('bell', 0.9);
          }
          break;
        case 'spawn': {
          const en = this.o.state.enemies.find((x) => x.id === e.id);
          if (en && (en.kind === 'witch' || en.kind === 'shell' || en.kind === 'warden')) {
            void music.play('boss');
            audio.play(en.kind === 'witch' ? 'boss-witch' : 'boss-shell');
          }
          break;
        }
        case 'keep':
          audio.play('keep', 0.7);
          break;
        case 'struct':
          if (e.what === 'destroyed') audio.play('crumble');
          else if (e.what === 'built') audio.play('build');
          break;
        case 'skill':
          audio.play(SKILL_SFX[e.skill] ?? unSfx(e.skill) ?? 'skill');
          this.ultMoment(e.hero, e.skill);
          break;
        case 'down':
          audio.play('down');
          break;
      }
    }
  }

  private ultTimer = 0;
  /**
   * An ultimate is a moment (docs/PODERES.md §5): its name large for an instant under the class's seal, and
   * the screen's edge in the colour of its type. Everyone in the fight sees it.
   */
  private ultMoment(heroId: number, skill: string): void {
    const def = SKILLS[skill as SkillId];
    const hero = this.o.state.heroes.find((x) => x.id === heroId);
    if (!def || def.slot !== 2 || !hero || def.cls !== hero.cls) return;
    const look = lookOf(def.id, hero.forks?.[2] ?? null, glyphTurns(def.id, hero.glyphs?.[2]));
    this.ultEl.className = `ult-moment el-${look}`;
    clear(this.ultEl);
    this.ultEl.append(
      h('span.ult-seal', { html: CLASS_ICON[hero.cls] }),
      h('b', {}, t(`skill.${def.id}`)),
      h('span.small', {}, this.o.liveInfo?.names?.[hero.pid] ?? hero.name),
    );
    void this.ultEl.offsetWidth;
    this.ultEl.classList.add('show');
    window.clearTimeout(this.ultTimer);
    this.ultTimer = window.setTimeout(() => this.ultEl.classList.remove('show'), 1500);
  }

  private spawnNumbers(events: BattleEvent[]): void {
    let n = 0;
    for (const e of events) {
      if (n > 14) break;
      if (e.t === 'hit') {
        const p = this.view.positions.get(e.id);
        if (!p) continue;
        const isHero = this.o.state.heroes.some((x) => x.id === e.id);
        const isEnemy = !isHero && this.o.state.enemies.some((x) => x.id === e.id);
        if (!isHero && !isEnemy) continue;
        // A hero's critical: a bigger number that jumps.
        // Its damage type colours it (fire orange, frost pale blue…).
        this.addNumber(e.crit ? `${e.dmg}!` : String(e.dmg), p.clone().setY(p.y + (e.crit ? 1.05 : 0.9)), `${isHero ? 'hero' : e.crit ? 'crit' : ''}${e.dt ? ` t-${e.dt}` : ''}`);
        n++;
      } else if (e.t === 'heal') {
        const p = this.view.positions.get(e.id);
        if (!p || e.amount <= 0) continue;
        this.addNumber(`+${e.amount}`, p.clone().setY(p.y + 1.1), 'heal');
        n++;
      } else if (e.t === 'keep') {
        this.addNumber(`−${e.dmg}`, this.view.keepPos.clone().setY(2.2), 'keep');
      }
    }
  }

  private addNumber(text: string, pos: THREE.Vector3, cls: string): void {
    const el = h('div.dmg', {}, text);
    for (const c of cls.split(' ')) if (c) el.classList.add(c);
    pos.x += (Math.random() - 0.5) * 0.3;
    const d = { el, pos, t: 0 };
    // Placed as it is made: live, the numbers come with a tick from the room, between the loop's drawings, and one
    // the page painted before the next (a frame the 30 fps cap skips) stood at the screen's top left corner.
    this.placeNumber(d);
    this.dmgLayer.append(el);
    this.dmg.push(d);
    if (this.dmg.length > 60) this.dmg.shift()?.el.remove();
  }

  private readonly numberAt = new THREE.Vector3();

  /** A number where its point is on screen, risen and faded by its age. */
  private placeNumber(d: { el: HTMLElement; pos: THREE.Vector3; t: number }): void {
    const v = this.numberAt.copy(d.pos).setY(d.pos.y + d.t * 0.8).project(this.cam.camera);
    const x = (v.x * 0.5 + 0.5) * viewW();
    const y = (-v.y * 0.5 + 0.5) * viewH();
    d.el.style.transform = `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px) translate(-50%, -50%) scale(${(1 - d.t * 0.3).toFixed(2)})`;
    d.el.style.opacity = String(Math.min(1, (0.9 - d.t) * 3));
  }

  // ---------- frame ----------

  private mist: THREE.Fog | null = null;
  private dg: DungeonHud | ExpeditionHud | FurrowsHud | null = null;
  /** The zoom to go back to once a puzzle has shown its order. */
  private framedDist: number | null = null;

  /** While a deep crypt puzzle shows its order (and a moment before; the glass bridge, until it is crossed), where to look and how far back to see all of it. */
  private puzzleWatch(): { at: THREE.Vector3; dist: number } | null {
    const s = this.o.state;
    const d = s.dungeon;
    const L = s.map.dungeon;
    if (!d || !L || d.room < 0) return null;
    const p = d.puzzles?.find((x) => x.room === d.room && !x.solved);
    // The glass bridge stays framed until it is crossed: the path is remembered against the whole room.
    if (!p || (p.kind !== 'runes' && p.kind !== 'glass') || (p.kind === 'runes' && (s.tick < p.showFrom - 12 || s.tick > p.showUntil + 6))) return null;
    const room = L.rooms[p.room];
    if (!room) return null;
    const c = hexToWorldFixed(room.q, room.r);
    const portrait = viewW() < viewH();
    return { at: simToWorld(c.x, c.y), dist: portrait ? 30 : 22 };
  }
  private party: PartyFrames | null = null;
  /** Live with friends: signals to the group (the wheel, the marks on the board). */
  private signals: Signals | null = null;

  /** The scene's shaders, sent to compile before anything is drawn (see firstDrawn), and whether they are linked. */
  private firstCompile: { at: number; done: boolean } | null = null;

  /**
   * Whether the board may be drawn yet. Its first drawing compiled every shader the fight's scene needs at once, and
   * a phone without parallel compiling (most Android phones) stood still a second or more as a crypt's waiting room
   * opened (1.1 s on an Adreno 740, up to 1.3 s in players' reports). Instead they are sent to compile first
   * (Post.compile waits on the GPU without blocking) and the board is drawn once they are linked: meanwhile the
   * screen is dark, the waiting room and its buttons answer. Never longer than FIRST_MAX_MS.
   */
  private firstDrawn(): boolean {
    const now = performance.now();
    if (!this.firstCompile) {
      const f = { at: now, done: false };
      this.firstCompile = f;
      const r = this.o.renderer;
      r.setRenderTarget(null);
      r.clear();
      void this.post.compile().then(
        () => (f.done = true),
        () => (f.done = true),
      );
      return false;
    }
    if (!this.firstCompile.done && now - this.firstCompile.at < FIRST_MAX_MS) return false;
    this.firstCompile.done = true;
    return true;
  }

  private frame(dt: number): void {
    if (this.loop.held) {
      // Baked, then linked: the shaders compile one by one after the bake (BattleView.frame), and a phone without
      // parallel compiling stalled on them as the first packs came in. Never more than HOLD_MAX_MS, whatever happens.
      this.heldSince ||= performance.now();
      this.loop.held = (this.view.bakeAhead(this.o.state, 30) || !this.view.readyAhead) && performance.now() - this.heldSince < HOLD_MAX_MS;
      // A quarter of a second still or more (a slow phone): say why, so the still board doesn't read as stuck.
      this.prepEl.classList.toggle('show', this.loop.held && performance.now() - this.heldSince > 250);
    }
    this.view.leading = this.predict.busy;
    this.view.interpolate(this.o.state, this.loop.alpha);
    // The mist follows the camera: it thins the edges whatever the zoom, never the ground under the hero.
    // In a bastion wave, farther out (as over the bastion): the fog shortens the towers, it doesn't hide the field.
    if (this.mist) {
      const wave = this.o.state.mode === 'wave' || !!this.o.state.rules?.towerRangeBp;
      this.mist.near = this.cam.dist * (wave ? 0.8 : 0.6);
      this.mist.far = this.cam.dist * (wave ? 2.1 : 1.45);
    }
    this.o.renderer.info.autoReset = false;
    this.o.renderer.info.reset();
    if (this.dg) {
      const meHero = this.me();
      const p = meHero ? this.view.positions.get(meHero.id) : undefined;
      // The deep crypt shows an order (the runes, the glass path): the camera takes in the whole room meanwhile.
      const watch = this.puzzleWatch();
      if (watch) {
        if (this.framedDist === null) this.framedDist = this.cam.dist;
        this.cam.focus(watch.at);
        this.cam.setDistance(watch.dist);
      } else {
        if (this.framedDist !== null) this.cam.setDistance(this.framedDist);
        this.framedDist = null;
        // Down, the camera is yours: drag to watch the fight; once picked up it follows the hero again.
        if (p && meHero && meHero.downTicks <= 0) this.cam.focus(p.clone().setY(0));
      }
    }
    // Over: the camera turns slowly around the board under the end's title (going up, in the night of the bell's finale).
    if (this.ended) this.cam.rotate(dt * 0.06, performance.now() < this.finaleUntil ? dt * 0.05 : 0);
    if (performance.now() < this.finaleUntil) this.cam.setDistance(this.cam.dist + dt * 2.4);
    this.cam.update(dt);
    // Keyboard pan.
    this.view.frame(this.cam.camera, (part) => this.post.compile(part));
    if (this.firstDrawn()) {
      this.meter.begin();
      this.post.render();
      this.meter.end(performance.now(), this.loop.lastMs, this.loop.cap > 0);
    }
    this.dg?.place(this.view.positions, this.cam.camera);
    this.plunderWays?.place(this.view.positions, this.cam.camera);
    this.lastFoes?.place(this.view.positions, this.cam.camera);
    this.riftCue?.place(this.cam.camera);
    this.rescueWay?.place(this.cam.camera);
    // On terraced land the ways in are not drawn: nobody says where they come from.
    if (!this.o.land) this.pathTags.place(this.o.state.map, this.cam.camera, viewW(), viewH());
    this.nameTags.place(this.o.state.heroes, this.view.positions, this.cam.camera, viewW(), viewH());
    this.signals?.place(this.view.positions, this.cam.camera);
    this.updateNumbers(dt);
    if (this.o.state.tick !== this.lastHudTick) {
      this.lastHudTick = this.o.state.tick;
      this.updateHud();
    }
    this.updateHover();
    this.updateAim();
    this.updatePin();
    if (this.o.net) {
      this.o.net.fps = this.loop.fps;
      this.o.net.jank = Math.max(this.o.net.jank, this.meter.jank);
    }
    this.meter.jank = 0;
    if (this.perf) {
      const info = this.o.renderer.info;
      this.perf.textContent = `${this.loop.fps.toFixed(0)} fps\n${info.render.calls} calls\n${(info.render.triangles / 1000).toFixed(0)}k tris\n${this.o.state.enemies.length} enemies${this.o.net ? `\n${this.o.net.rtt} ms rtt\n${this.o.net.client.behind} behind` : ''}`;
    }
  }

  private pinWorld(target: PinTarget): THREE.Vector3 | null {
    const s = this.o.state;
    if (target === 'keep') return this.view.keepPos.clone().setY(2.6);
    if (target === 'gate') {
      const gate = s.map.sectors[0]?.gate;
      if (!gate) return null;
      const g = hexToWorldFixed(gate.q, gate.r);
      return simToWorld(g.x, g.y).setY(1.6);
    }
    const me = this.me();
    const p = me ? this.view.positions.get(me.id) : undefined;
    return p ? p.clone().setY(p.y + 1.5) : null;
  }

  /** The guide moves the table to what it is talking about. */
  private coachView(v: CoachView): void {
    const post = this.me()?.post ?? 0;
    if (v === 'overview') {
      this.frameSector(post);
      return;
    }
    const portrait = viewW() < viewH();
    let p: THREE.Vector3 | null;
    if (v === 'build') {
      // Between the gate and the keep: where the first towers belong.
      const gate = this.pinWorld('gate');
      p = gate ? gate.setY(0).multiplyScalar(0.72) : null;
    } else p = this.pinWorld(v);
    if (!p) return;
    p.setY(0);
    const dist = (v === 'build' ? 14 : v === 'hero' ? 10 : 12) * (portrait ? 1.35 : 1);
    // Phones show the guide at the bottom, desktops at the top: nudge the spot into the clear part of the screen.
    const toward = new THREE.Vector3(Math.sin(this.cam.yaw), 0, Math.cos(this.cam.yaw));
    p.addScaledVector(toward, dist * (portrait ? 0.16 : -0.06));
    this.cam.focus(p);
    this.cam.setDistance(dist);
  }

  private updatePin(): void {
    if (!this.coach) return;
    const target = this.coach.pinTarget();
    const w = target ? this.pinWorld(target) : null;
    this.coach.pin.classList.toggle('show', !!w);
    if (!w) return;
    const v = w.project(this.cam.camera);
    const x = (v.x * 0.5 + 0.5) * viewW();
    const y = (-v.y * 0.5 + 0.5) * viewH();
    this.coach.pin.style.transform = `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px)`;
  }

  private updateNumbers(dt: number): void {
    this.dmg = this.dmg.filter((d) => {
      d.t += dt;
      if (d.t > 0.9) {
        d.el.remove();
        return false;
      }
      this.placeNumber(d);
      return true;
    });
  }

  // ---------- HUD ----------

  private buildHud(): HTMLElement {
    const s = this.o.state;
    this.keepBar = h('i', { style: 'width:100%' });
    this.keepText = h('span');
    this.waveText = h('span.wt-sub');
    this.waveNum = h('span.wt-n');
    this.waveLabel = h('span.wt-label');
    this.waveFill = h('i', { style: 'width:0%' });
    // What's left of the wave, big and in the middle: the number, a progress bar and where they are.
    // Incursions lead with what is being extracted: the resource, how much so far and the pace.
    const ex = this.o.extraction;
    if (ex) this.waveIco = h('span.wt-ico', { html: ICONS[(ex.loot[0]?.[0] ?? 'stone') as 'stone'] });
    this.waveTrack = h(
      `div.chip.wave-track${ex ? '.extract' : ''}`,
      { 'data-test': 'wave-track', role: 'status', 'aria-live': 'polite' },
      h('div.wt-main', {}, this.waveIco ?? '', this.waveNum, this.waveLabel),
      h('div.bar.wt-bar', {}, this.waveFill),
      this.waveText,
      // The place's own rule stays in sight the whole fight (the opening banner is gone in seconds).
      ex && s.plunder ? h('div.wt-rule', { 'data-test': 'inc-rule' }, t('plunder.rule')) : ex && s.rules?.twist && s.rules.twist !== 'quarry' ? h('div.wt-rule', { 'data-test': 'inc-rule' }, t(`inc.rule.${s.rules.twist}`)) : '',
      ex && s.rules?.tier === 3 ? h('div.wt-rule.obsidian', { 'data-test': 'tier-rule' }, t('ilevel.3.rule')) : '',
      // A bet's rule, all fight long.
      s.rules?.bet ? (this.betRule = h('div.wt-rule.bet', { 'data-test': 'bet-rule' }, t(`bet.${s.rules.bet}.hud`, { n: s.alone?.crossed.length ?? 0, of: s.rules.alone?.max ?? 0 }))) : '',
      // A side saga's fight with clues: say so all fight long.
      (this.clueRule = s.rules?.clues?.length ? h('div.wt-rule.bet', { 'data-test': 'clue-rule' }, t('side.clueRule')) : null) ?? '',
      // The night of lanterns: how to tell them apart, and how many faces are off.
      s.rules?.crowd ? h('div.wt-rule.bet', { 'data-test': 'crowd-rule' }, t('side.crowdRule')) : '',
      s.rules?.lanterns ? h('div.wt-rule.bet', { 'data-test': 'lantern-rule' }, t('side.lanternRule')) : '',
      s.rules?.rescue ? (this.rescueRule = h('div.wt-rule.bet', { 'data-test': 'rescue-rule' }, t('side.rescueRule'))) : '',
      // The drive: how a herd is moved.
      s.rules?.herd ? h('div.wt-rule.bet', { 'data-test': 'herd-rule' }, t('side.herdRule')) : '',
      s.rules?.collapse ? h('div.wt-rule.bet', { 'data-test': 'collapse-rule' }, t('side.collapseRule')) : '',
      s.rules?.place === 'camp' && s.plunder ? h('div.wt-rule.bet', { 'data-test': 'camp-rule' }, t('side.campRule')) : '',
      s.rules?.trap ? h('div.wt-rule.bet', { 'data-test': 'trap-rule' }, t('side.trapRule')) : '',
      s.rules?.blast ? h('div.wt-rule.bet', { 'data-test': 'blast-rule' }, t('side.blastRule')) : '',
      s.rules?.rails ? h('div.wt-rule.bet', { 'data-test': 'rails-rule' }, t('side.railsRule')) : '',
      s.rules?.bells ? h('div.wt-rule.bet', { 'data-test': 'bells-rule' }, t('side.bellsRule')) : '',
      s.rules?.anvil ? h('div.wt-rule.bet', { 'data-test': 'anvil-rule' }, t('side.anvilRule')) : '',
      s.rules?.toll ? h('div.wt-rule.bet', { 'data-test': 'toll-rule' }, t('side.toll.rule')) : '',
      s.rules?.rising ? h('div.wt-rule.bet', { 'data-test': 'rising-rule' }, t('side.risingRule')) : '',
      s.rules?.well ? h('div.wt-rule.bet', { 'data-test': 'well-rule' }, t('side.wellRule')) : '',
      s.rules?.fair ? h('div.wt-rule.bet', { 'data-test': 'fair-rule' }, t('side.fairRule')) : '',
      s.rules?.tide ? h('div.wt-rule.bet', { 'data-test': 'tide-rule' }, t('side.tideRule')) : '',
      s.rules?.neck ? (this.neckRule = h('div.wt-rule.bet', { 'data-test': 'neck-rule' }, neckLine(s))) : '',
      s.rules?.climb ? (this.climbRule = h('div.wt-rule.bet', { 'data-test': 'climb-rule' }, climbLine(s))) : '',
      s.rules?.winch ? h('div.wt-rule.bet', { 'data-test': 'winch-rule' }, t('side.hang.rule')) : '',
      s.rules?.fin ? h('div.wt-rule.bet', { 'data-test': 'fin-rule' }, t(s.rules.fin.breath ? 'side.breathRule' : s.rules.sunken?.boss ? 'side.sunkenRule' : 'side.finRule')) : '',
      s.rules?.maw ? h('div.wt-rule.bet', { 'data-test': 'maw-rule' }, t('side.mawRule')) : '',
      s.rules?.sunken?.heart ? h('div.wt-rule.bet', { 'data-test': 'heart-rule' }, t(s.rules.fin?.breath ? 'side.sunken.heartFire' : 'side.sunken.heartBait')) : '',
      // The dragon: the old harpoons on the rim (the first Vigil's, to a group that read its diary), and after a try lost
      // to much fire, what it does to it.
      s.rules?.dragon ? h('div.wt-rule.bet.dragon-intro', { 'data-test': 'dragon-rule' }, t(s.rules.dragon.cracked ? 'saga.burn.rule.vigil' : 'saga.burn.rule.old')) : '',
      s.rules?.dragon?.fireWarn ? h('div.wt-rule.bet.hot.dragon-intro', { 'data-test': 'dragon-fire' }, t('saga.burn.rule.fire')) : '',
      // The dragon, after La Quemadura: what the wayfarers' saga brought to this fight.
      (s.rules?.dragon && (s.rules.dragon.warnBp || s.rules.dragon.cracked || s.rules.dragon.beaconR || s.rules.dragon.harpoons.length > DRAGON.harpoons.n)) || s.heroes.some((x) => x.pid === 'npc:orla')
        ? h(
            `div.wt-rule.bet${s.rules?.dragon ? '.dragon-intro' : ''}`,
            { 'data-test': 'burn-edges' },
            t('side.burn.edges', {
              list: [s.rules?.dragon?.warnBp ? t('side.burn.edge.map') : '', (s.rules?.dragon?.harpoons.length ?? 0) > DRAGON.harpoons.n ? t('side.burn.edge.ballista') : '', s.rules?.dragon?.cracked ? t('side.burn.edge.scale') : '', s.rules?.dragon?.beaconR ? t('side.burn.edge.beacon') : '', s.heroes.some((x) => x.pid === 'npc:orla') ? t('side.burn.edge.orla') : ''].filter(Boolean).join(' · '),
            }),
          )
        : '',
      // The lash: what to do when the ground creaks, and when it next will.
      s.rules?.lash ? h('div.wt-rule.bet', { 'data-test': 'lash-rule' }, t('side.lashRule')) : '',
      s.rules?.lash ? (this.lashEl = h('div.wt-rule', { 'data-test': 'lash-next' })) : '',
      // The hunt: its rule, and how it goes.
      s.rules?.hunt ? h('div.wt-rule.bet', { 'data-test': 'hunt-rule' }, t('side.huntRule')) : '',
      s.rules?.call ? h('div.wt-rule.bet', { 'data-test': 'call-rule' }, t('side.callRule')) : '',
      s.rules?.hunt ? (this.huntEl = h('div.wt-rule', { 'data-test': 'hunt-state' })) : '',
      s.rules?.copies ? h('div.wt-rule.bet', { 'data-test': 'copies-rule' }, t('side.copies.rule')) : '',
      s.rules?.copies ? (this.copiesEl = h('div.wt-rule.copies', { 'data-test': 'copies-state' })) : '',
    );
    if (s.rules?.burst) this.waveTrack.classList.add('burst');
    const inc = s.mode === 'incursion';
    this.extractBar = h('i', { style: 'width:0%' });
    this.nightBoss = s.mode === 'wave' && !!s.rules?.night && s.wave.spawns.some((sp) => sp.boss && sp.kind === 'shell');
    // Saga acts: an escort shows its caravan and how far along the road it is; a boss act, the boss's life.
    const escort = !!s.rules?.escort;
    const boss = !!s.rules?.bossWins;
    // A hundred at a blow: the clearing can't fall in earnest; only the time left is shown.
    const burst = !!s.rules?.burst;
    // The drive has no node to keep either: the herd is the fight.
    const noKeep = burst || !!s.rules?.herd || !!s.rules?.collapse || !!s.rules?.trap || !!s.rules?.well || !!s.rules?.fair || !!s.rules?.dragon;
    const keep = h(
      'div.chip.keep-card',
      // Nothing to show there (the well and the fair have no node and no extraction): the empty card goes.
      s.rules?.herd || s.rules?.collapse || s.rules?.well || s.rules?.fair ? { hidden: true } : {},
      noKeep ? '' : h('div.row', {}, h('span.label', {}, h('span.ico', { html: s.rules?.bet === 'barrel' ? ICONS.cask : ICONS.keep }), h('span.txt', {}, escort ? t(s.rules?.bet === 'barrel' ? 'bet.cart' : s.rules?.rails ? 'side.railsTrain' : s.rules?.climb ? 'side.climb.bell' : 'saga.cart') : s.plunder ? t(s.rules?.place === 'camp' ? 'side.campCart' : 'plunder.cart') : s.rules?.blast ? t('side.blastTower') : s.rules?.anvil ? t('side.anvilNode') : s.rules?.toll ? t('side.toll.node') : s.rules?.rising ? t('side.risingNode') : s.rules?.tide || s.rules?.fin ? t('side.tideNode') : s.rules?.maw ? t('side.mawNode') : s.rules?.winch ? t('side.hang.frame') : inc ? t('inc.node') : t('hud.keep'))), this.keepText),
      noKeep ? '' : h('div.bar', {}, this.keepBar),
      inc && !s.rules?.rescue && !s.rules?.herd && !s.rules?.blast && !s.rules?.bells && !s.rules?.anvil && !s.rules?.toll && !s.rules?.well && !s.rules?.fair && !s.rules?.tide && !s.rules?.fin && !s.rules?.maw && !s.rules?.winch ? h('div.row', {}, s.rules?.copies ? (this.copiesLbl = h('span.label', {}, t('side.copies.none'))) : h('span.label', {}, escort ? t(s.rules?.rails ? 'side.railsWay' : s.rules?.climb ? 'side.climb.way' : 'saga.road') : s.rules?.hunt ? t('enemy.uro') : s.rules?.place === 'grave' && boss ? t('enemy.ragna') : s.rules?.rising && boss ? t(s.wave.spawns.some((sp) => sp.boss && sp.kind === 'hulk') ? 'enemy.hulk' : 'enemy.bruni') : s.rules?.trap && boss ? t('enemy.colossus') : boss && s.wave.spawns.some((sp) => sp.boss && sp.kind === 'dragon') ? t('saga.burn.dragon.title') : boss ? t('saga.bossHp') : burst ? t('bet.hud.time') : s.plunder ? t(s.rules?.place === 'camp' ? 'side.campGolem' : 'plunder.golem') : t('inc.extract')), s.plunder ? this.golemEl : '') : '',
      inc && !s.rules?.rescue && !s.rules?.herd && !s.rules?.blast && !s.rules?.bells && !s.rules?.anvil && !s.rules?.toll && !s.rules?.well && !s.rules?.fair && !s.rules?.tide && !s.rules?.fin && !s.rules?.maw && !s.rules?.winch ? h(`div.bar.objective${boss || s.rules?.copies ? '.boss' : ''}`, {}, this.extractBar) : '',
      // The dragon of the Burn: its six scales beside its life, and the glass's cold.
      s.rules?.dragon ? this.dragonHud() : '',
      // The night of the bell at dusk (El Párpado 4): the Hollow Shell's life, on the group's own bastion.
      this.nightBoss ? h('div.row', {}, h('span.label', {}, t('enemy.shell'))) : '',
      this.nightBoss ? h('div.bar.objective.boss', { 'data-test': 'night-boss' }, this.extractBar) : '',
    );
    const res = h('div.res');
    if (s.purse) {
      for (const k of ['wood', 'stone', 'crystal'] as const) {
        const val = h('span', {}, '0');
        const chip: HTMLElement = h('div.chip', { title: t(`res.${k}`), onclick: () => showResTip(chip, k, Number(val.textContent), 'battle') }, h('span', { html: ICONS[k] }), h('span.rname', {}, t(`res.${k}`)), val);
        this.resEls[k] = val;
        res.append(chip);
      }
    }
    const pauseBtn = this.o.allowPause
      ? h('button.iconbtn', { 'aria-label': t('hud.pause'), onclick: () => this.openPause(), html: ICONS.pause })
      : null;
    const speedBtn = this.o.allowPause
      ? h('button.iconbtn', {
          'aria-label': t('hud.speed', { n: 2 }),
          html: ICONS.speed,
          onclick: (ev: Event) => {
            this.loop.speed = this.loop.speed === 1 ? 2 : 1;
            (ev.currentTarget as HTMLElement).style.color = this.loop.speed === 2 ? 'var(--accent)' : '';
            audio.play('click');
          },
        })
      : null;
    const helpBtn =
      this.o.coach !== undefined && !s.started
        ? h('button.iconbtn', {
            'aria-label': t('coach.title'),
            'data-test': 'coach-help',
            onclick: () => {
              audio.play('click');
              if (!this.o.state.started) this.coach?.begin();
            },
          }, '?')
        : null;
    const right = h('div', { style: 'display:flex;gap:8px;align-items:flex-start' }, res, helpBtn, musicToggle(), speedBtn, pauseBtn);
    const top = h('div.topbar', {}, keep, right);
    // A holiday season says so under the title (the fight carries it from the moment it was set up).
    const season = s.season;
    this.banner = h(
      `div.wave-banner${season ? `.season.${season}` : ''}`,
      {},
      h('div.big', {}, this.o.title),
      this.o.subtitle ? h('div.small', {}, this.o.subtitle) : null,
      season ? h('div.small.season-line', {}, h('span', { html: ICONS.pumpkin }), t(`season.${season}.short`)) : null,
      this.o.state.rules?.keepFrailBp ? h('div.small.season-line.frail', {}, h('span', { html: ICONS.keep }), t('great.battleFrail')) : null,
    );
    this.toastEl = h('div.toast', { onclick: () => this.toastEl.classList.remove('show') });
    this.ultEl = h('div.ult-moment', { 'aria-hidden': 'true' });
    this.prepEl = h('div.preparing', { role: 'status', 'data-test': 'preparing' }, h('i.prep-dot', { 'aria-hidden': 'true' }), h('span', {}, t('hud.preparing')));
    this.downNum = h('span.dn-n');
    this.downFill = h('i', { style: 'width:0%' });
    this.downEl = h('div.chip.down-track', { role: 'status', 'aria-live': 'assertive', 'data-test': 'down-track' }, h('div.dn-main', {}, h('span.dn-label', {}, t('hud.downTitle')), this.downNum), h('div.bar.dn-bar', {}, this.downFill), h('span.dn-sub', {}, t('hud.downSub')));
    const hud = h('div.hud', {}, this.pathTags.el, this.nameTags.el, top, this.waveTrack, this.downEl, this.banner, this.ultEl, this.prepEl, this.toastEl);
    // Hero card + skills.
    const me = this.me();
    if (me) {
      this.heroBar = h('i', { style: 'width:100%' });
      this.heroText = h('span');
      hud.append(
        h('div.chip.hero-card', {}, h('div.row', {}, h('span.name', {}, me.name || t(`class.${me.cls}`)), this.heroText), h('div.bar.green', {}, this.heroBar)),
      );
      const skills = h('div.skills');
      const keys = ['Q', 'W', 'E'];
      [0, 1, 2].forEach((i) => {
        const btn = h(`button.skill${i === 2 ? '.ult' : ''}`, {}, h('span.face'), h('span.cd'), isTouch() ? null : h('span.key', {}, keys[i] as string));
        // The numbers as this hero has them (ranks and fork from its tree), not the base ones.
        this.pressable(btn, () => this.useSkill(i as 0 | 1 | 2, true), () => {
          const h = this.me() ?? me;
          const id = kitOf(h)[i] as SkillId;
          const sk = levelSkill(rankedSkill(SKILLS[id], h.ranks?.[i] ?? 1, h.forks?.[i] ?? null, h.glyphs?.[i] ?? null), h.level);
          return [t(`skill.${id}`), `${t(`skill.${id}.desc`, { n: sk.amount, s: sk.duration / 12 })} ${skillFacts(id, sk, h.stats.cooldownBp)}`];
        });
        this.skillEls.push(btn);
        skills.append(btn);
      });
      if (me.cls === 'artificer') {
        // Overload has its own button, next to the turret's: placing never blows one up.
        const ob = h('button.skill.overload', { 'aria-label': t('skill.overload'), 'data-test': 'overload' }, h('span.face', { html: ICONS.overload }), h('span.cd'), isTouch() ? null : h('span.key', {}, 'F'));
        this.pressable(ob, () => this.useOverload(true), () => [t('skill.overload'), t('skill.overload.desc', { s: ARTIFICER.overload.cooldown / 12 })]);
        skills.insertBefore(ob, this.skillEls[0] ?? null);
        this.overloadBtn = ob;
      }
      if (s.plunder) {
        const lb = h('button.skill.leave', { 'aria-label': t('plunder.leave'), 'data-test': 'leave' }, h('span.face', { html: ICONS.cart }), h('span.carry-lbl', {}, t('plunder.leave')), isTouch() ? null : h('span.key', {}, 'V')) as HTMLButtonElement;
        this.pressable(lb, () => this.leave(), () => [t('plunder.leave'), t('plunder.leaveTip', { n: PLUNDER.winLoad })]);
        skills.prepend(lb);
        this.leaveBtn = lb;
      }
      if (s.rules?.hunt) {
        // The hunt: a trap at the hero's feet, and home to the camp (the wounds stay).
        const lb = h('button.skill.leave', { 'aria-label': t('side.hunt.leave'), 'data-test': 'leave' }, h('span.face', { html: ICONS.keep }), h('span.carry-lbl', {}, t('side.hunt.leave')), isTouch() ? null : h('span.key', {}, 'V')) as HTMLButtonElement;
        this.pressable(lb, () => this.leave(), () => [t('side.hunt.leave'), t('side.hunt.leaveTip')]);
        skills.prepend(lb);
        this.leaveBtn = lb;
        const tb = h('button.skill.carry', { 'aria-label': t('side.hunt.trap'), 'data-test': 'trap' }, h('span.face', { html: ICONS.snare }), h('span.carry-lbl', {}, t('side.hunt.trap')), h('span.tcount'), isTouch() ? null : h('span.key', {}, 'C')) as HTMLButtonElement;
        this.pressable(tb, () => this.trap(), () => [t('side.hunt.trap'), t('side.hunt.trapTip', { n: s.rules?.hunt?.trap.per ?? 0 })]);
        skills.prepend(tb);
        this.trapBtn = tb;
      }
      if (s.rules?.anvil || s.rules?.toll) {
        // The five strikes: the hammer, by the Anvil. The bell at midnight: the same hammer, on the bell.
        const k = s.rules?.toll ? 'side.toll' : 'side.anvil';
        const sb = h('button.skill.carry', { 'aria-label': t(`${k}.strike`), 'data-test': 'strike' }, h('span.face', { html: ICONS.hammer }), h('span.carry-lbl', {}, t(`${k}.strike`)), isTouch() ? null : h('span.key', {}, 'C')) as HTMLButtonElement;
        this.pressable(sb, () => this.strike(), () => [t(`${k}.strike`), t(`${k}.strikeTip`)]);
        skills.prepend(sb);
        this.strikeBtn = sb;
      }
      if (s.dungeon?.exp || s.plunder) {
        const cb = h('button.skill.carry', { 'aria-label': t('exp.carry'), 'data-test': 'carry' }, h('span.face', { html: ICONS.carry }), h('span.carry-lbl', {}, t('exp.carry')), isTouch() ? null : h('span.key', {}, 'C')) as HTMLButtonElement;
        this.pressable(cb, () => this.carry(), () => [t('exp.carry'), t('exp.carryTip')]);
        skills.prepend(cb);
        this.carryBtn = cb;
      }
      if (s.mode === 'incursion' && this.turretMax() > 0) {
        const tb = h('button.skill', { 'aria-label': t('inc.turret'), 'data-test': 'turret' }, h('span.face', { html: ICONS.fieldTurret }), h('span.tcount'), isTouch() ? null : h('span.key', {}, 'R'));
        this.pressable(tb, () => this.toggleTurret(), () => [t('inc.turret'), t('inc.turretDesc', { n: this.turretMax() - (this.me()?.turretsPlaced ?? 0), s: Math.round((this.o.state.rules?.turrets?.ticks ?? 0) / 12) })]);
        skills.prepend(tb);
        this.turretBtn = tb;
      }
      this.skillsEl = skills;
      this.fitSkills(me);
      hud.append(skills);
    }
    if (this.o.sandbox) {
      const dock = h('div.dock');
      for (const type of this.o.buildable ?? []) {
        const def = STRUCTURES[type];
        const cost = h('div.cost');
        for (const k of ['wood', 'stone', 'crystal'] as const) {
          if (def.cost[k] > 0) cost.append(h('span', {}, h('span', { html: ICONS[k] }), String(def.cost[k])));
        }
        const thumb = this.o.thumbs?.[type];
        const card = h(
          'button.card',
          { 'aria-label': t(`structure.${type}`), 'data-build': type },
          h('div.pic', {}, thumb ? h('img', { src: thumb, alt: '' }) : null),
          h('div.nm', {}, t(`structure.${type}`)),
          cost,
        );
        card.addEventListener('pointerdown', (ev) => this.cardDown(ev, type, card));
        card.addEventListener('pointerenter', () => this.tooltip(card, t(`structure.${type}`), t(`structure.${type}.desc`)));
        card.addEventListener('pointerleave', () => this.tooltip(null));
        this.cards.push({ type, el: card });
        dock.append(card);
      }
      if (this.o.land) {
        const eraser = h('button.card', { 'aria-label': t('land.erase'), 'data-test': 'erase' }, h('div.pic', { html: ICONS.close ?? '' }), h('div.nm', {}, t('land.erase')), h('div.cost'));
        eraser.addEventListener('click', () => this.toggleEraser(eraser));
        dock.append(eraser);
      }
      hud.append(dock);
      if (!this.o.state.started) {
        this.startWrap = h('div.start-wrap', {}, h('p', {}, t('hud.startHint')), h('button.btn', { onclick: () => this.startWave() }, t('hud.start')));
        hud.append(this.startWrap);
      }
    }
    this.flashBanner(this.o.title, this.o.subtitle, this.o.sandbox && this.o.coach === undefined ? 0 : 3500);
    return hud;
  }

  /**
   * The buttons dressed for the skills the hero carries. A waiting room rebuilds the fight when a
   * player changes what they carry (docs/PODERES.md §3), so this runs again on every reset.
   */
  private fitSkills(me: Hero): void {
    const kit = kitOf(me) as SkillId[];
    this.skillEls.forEach((btn, i) => {
      const id = kit[i] as SkillId;
      const g = me.glyphs?.[i] ?? null;
      if (btn.dataset.skill === id && (btn.dataset.glyph ?? '') === (g ?? '')) return;
      btn.dataset.skill = id;
      btn.setAttribute('aria-label', t(`skill.${id}`));
      (btn.querySelector('.face') as HTMLElement).innerHTML = ICONS[id];
      // The glyph carved on the button (docs/GLIFOS.md §4), small on its rim.
      btn.querySelector('.sk-glyph')?.remove();
      if (g) {
        btn.dataset.glyph = g;
        btn.append(h('span.sk-glyph', { html: ICONS[g], title: t(`glyph.${g}`), 'aria-hidden': 'true' }));
      } else delete btn.dataset.glyph;
    });
    this.overloadBtn?.toggleAttribute('hidden', kit[0] !== 'turret');
    if (this.skillsEl) this.skillsEl.style.gridTemplateColumns = `repeat(${[...this.skillsEl.children].filter((c) => !(c as HTMLElement).hidden).length}, auto)`;
  }

  private startWave(): void {
    if (!this.startWrap) return;
    this.hud.querySelector('[data-test="coach-help"]')?.remove();
    this.send({ t: 'start' });
    this.startWrap.remove();
    this.startWrap = null;
    audio.play('horn');
    this.flashBanner(this.o.title, this.o.subtitle);
  }

  private tipEl: HTMLElement | null = null;
  /**
   * A skill button: with a mouse it fires on press and explains itself on hover; on a touch screen a
   * tap fires it and holding it shows what it does instead (and doesn't fire).
   */
  private pressable(btn: HTMLElement, use: () => void, tip: () => [string, string]): void {
    let timer = 0;
    let held = false;
    let start = { x: 0, y: 0 };
    let dragged = false;
    btn.addEventListener('pointerdown', (ev) => {
      ev.stopPropagation();
      ev.preventDefault();
      if (ev.pointerType === 'mouse') {
        use();
        return;
      }
      held = false;
      dragged = false;
      start = { x: ev.clientX, y: ev.clientY };
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        held = true;
        navigator.vibrate?.(10);
        const [title, body] = tip();
        this.tooltip(btn, title, body, true);
      }, 420);
    });
    // A finger that slides off the button onto the board aims as it goes and places (or casts) where it lifts.
    btn.addEventListener('pointermove', (ev) => {
      if (ev.pointerType === 'mouse' || held || !(ev.buttons & 1)) return;
      if (!dragged && Math.hypot(ev.clientX - start.x, ev.clientY - start.y) > DRAG_START) {
        window.clearTimeout(timer);
        dragged = true;
        use();
      }
      if (dragged) this.mouse = { x: ev.clientX, y: ev.clientY, inside: !btn.contains(document.elementFromPoint(ev.clientX, ev.clientY)) };
    });
    const release = (ev: PointerEvent, fire: boolean): void => {
      if (ev.pointerType === 'mouse') return;
      window.clearTimeout(timer);
      if (held) this.tooltip(null);
      else if (dragged) {
        this.mouse.inside = false;
        const over = document.elementFromPoint(ev.clientX, ev.clientY);
        // Let go back on the button: aiming stays on, for a tap on the board as before.
        if (fire && (this.placingTurret || this.overloading || this.aiming >= 0) && over && !btn.contains(over) && !over.closest('.skills')) this.tap(ev.clientX, ev.clientY);
      } else if (fire) use();
      held = false;
      dragged = false;
    };
    btn.addEventListener('pointerup', (ev) => {
      ev.stopPropagation();
      release(ev, true);
    });
    btn.addEventListener('pointercancel', (ev) => release(ev, false));
    btn.addEventListener('pointerenter', (ev) => {
      if (ev.pointerType !== 'mouse') return;
      const [title, body] = tip();
      this.tooltip(btn, title, body);
    });
    btn.addEventListener('pointerleave', (ev) => {
      if (ev.pointerType === 'mouse') this.tooltip(null);
    });
  }

  private tooltip(anchor: HTMLElement | null, title = '', body = '', touch = false): void {
    this.tipEl?.remove();
    this.tipEl = null;
    if (!anchor || (isTouch() && !touch)) return;
    const r = anchor.getBoundingClientRect();
    const tip = h('div.tooltip', {}, h('b', {}, title), h('p', {}, body));
    this.el.append(tip);
    const tr = tip.getBoundingClientRect();
    tip.style.left = `${Math.min(viewW() - tr.width - 8, Math.max(8, r.left + r.width / 2 - tr.width / 2))}px`;
    tip.style.top = `${r.top - tr.height - 8}px`;
    this.tipEl = tip;
  }

  private flashBanner(title: string, sub?: string, ms = 3200): void {
    clear(this.banner);
    this.banner.append(h('div.big', {}, title), sub ? h('div.small', {}, sub) : '');
    this.banner.style.opacity = '1';
    this.banner.style.transition = 'opacity .6s';
    if (ms > 0) window.setTimeout(() => (this.banner.style.opacity = '0'), ms);
  }

  private aimEl: HTMLElement | null = null;
  private aimTimer = 0;

  /**
   * How to aim, said small at the top of the field and never in the way of the tap that aims (it takes no
   * touches); only the first few times for each kind, then the ring on the ground says it all.
   */
  private aimHint(kind: string, msg: string): void {
    const key = `somnhold.aimHint.${kind}`;
    try {
      const n = Number(localStorage.getItem(key) ?? '0');
      if (n >= 3) return;
      localStorage.setItem(key, String(n + 1));
    } catch {
      /* no storage: show it */
    }
    if (!this.aimEl) {
      this.aimEl = h('div.aim-hint', { role: 'status' });
      this.hud.append(this.aimEl);
    }
    this.aimEl.textContent = msg;
    this.aimEl.classList.add('show');
    window.clearTimeout(this.aimTimer);
    this.aimTimer = window.setTimeout(() => this.aimEl?.classList.remove('show'), 2400);
  }

  private signalHintEl: HTMLElement | null = null;

  /** Signals: the wheel, the marks, and what each one wires into on this screen. */
  private buildSignals(net: NonNullable<BattleScreenOpts['net']>): void {
    const sig = new Signals({
      me: this.o.me,
      names: this.o.liveInfo?.names ?? {},
      state: () => this.o.state,
      send: (x) => net.client.signal(x),
      spotAt: (x, y) => {
        const g = this.groundAt(x, y);
        if (!g) return null;
        const sp = worldToSim(g);
        const enemy = this.pickEnemy(x, y);
        return { x: sp.x, y: sp.y, enemy, hero: enemy ? 0 : this.pickHero(x, y) };
      },
      walkTo: (x, y) => {
        if (this.me()?.downTicks) return;
        this.send({ t: 'move', x, y });
      },
      vfx: this.view.vfx,
      hint: (msg) => {
        if (!this.signalHintEl) {
          this.signalHintEl = h('div.aim-hint.sig-hint', { role: 'status' });
          this.hud.append(this.signalHintEl);
        }
        if (msg) this.signalHintEl.textContent = msg;
        this.signalHintEl.classList.toggle('show', !!msg);
      },
      flag: (pid) => {
        this.party?.flag(pid);
        this.dg?.party.flag(pid);
      },
      note: (pid, text, color) => this.chat?.note(pid, text, color),
      tip: (msg) => this.toast(msg, 6500),
    });
    this.signals = sig;
    net.client.onSignal = (line) => sig.receive(line);
    this.hud.prepend(sig.layer);
    this.hud.append(sig.el);
    sig.setVisible(false);
    // Right above the hero's card, whatever its height (long names wrap) and the screen's.
    const card = this.hud.querySelector<HTMLElement>('.hero-card');
    if (card) {
      const place = (): void => {
        const r = card.getBoundingClientRect();
        if (r.height > 0) sig.el.style.setProperty('--sig-b', `${Math.round(viewH() - r.top + 10)}px`);
      };
      new ResizeObserver(place).observe(card);
      window.addEventListener('resize', place);
      this.disposers.push(() => window.removeEventListener('resize', place));
      place();
    }
  }

  toast(msg: string, ms = 1800): void {
    this.toastEl.textContent = msg;
    // On a phone it sits under the top card, however tall that card's rule lines made it.
    this.toastEl.style.top = '';
    const track = this.waveTrack?.isConnected && this.waveTrack.offsetParent ? this.waveTrack.getBoundingClientRect() : null;
    if (track && viewW() <= 640 && getComputedStyle(this.toastEl).top !== 'auto' && this.toastEl.getBoundingClientRect().top < track.bottom + 8) this.toastEl.style.top = `${Math.round(track.bottom + 8)}px`;
    this.toastEl.classList.add('show');
    window.clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => this.toastEl.classList.remove('show'), ms);
  }

  private lobbyEl: HTMLElement | null = null;

  private updateLobby(): void {
    const net = this.o.net;
    const info = this.o.liveInfo;
    if (!net || !info) return;
    const s = this.o.state;
    const names = net.client.roster.players.map((p) => info.names[p] ?? p).join(', ');
    if (s.mode === 'dungeon' || s.mode === 'incursion') {
      this.cryptLobby(net.client.roster.players, info.names);
      return;
    }
    if (!s.started) {
      // Built once per roster change; only the countdown ticks in place (a rebuild per tick ate clicks).
      const players = net.client.roster.players;
      const key = `wave|${players.join(',')}`;
      if (!this.lobbyEl || this.lobbyKey !== key) {
        this.lobbyKey = key;
        const { body, foot } = this.lobbyCard(players);
        this.lobbyClock = h('b.lc-time');
        body.append(
          h(
            'div.lobby-head',
            {},
            h('div', {}, h('h3', {}, t('live.lobbyTitle')), h('p.lobby-sub', {}, `${s.wave.spawns.length} ${t('hud.waveBrings')}`)),
            h('div.lobby-clock', { role: 'timer', 'aria-label': t('live.startsIn') }, h('span.small', {}, t('live.startsIn')), this.lobbyClock),
          ),
          this.partyList(players, info.names),
        );
        foot.append(h('p', {}, t('live.lobbyHint')), h('button.btn.small.ghost', { 'data-test': 'lobby-leave', onclick: () => this.o.onExit() }, t('party.leave')));
      }
      const left = Math.max(0, info.due - info.now());
      this.countdown(left);
    } else if (this.lobbyEl) {
      this.closeLobby();
      this.countEl?.remove();
      this.countEl = null;
      audio.play('horn');
      this.flashBanner(this.o.title, t('live.together', { names }));
    }
  }

  private chat: ChatBox | null = null;
  /** The presence poll: who is around while gathering, the keep's chat all along. */
  private socialTimer = 0;
  private lobbyBody: HTMLElement | null = null;
  private lobbyFoot: HTMLElement | null = null;

  /**
   * The waiting room is one card: what this room is about (rebuilt when someone comes or goes), then the
   * chat (kept, so a half-typed line survives), what to carry and the rest of the group, then the way out.
   */
  private lobbyCard(players: string[]): { body: HTMLElement; foot: HTMLElement } {
    if (!this.lobbyEl || !this.lobbyBody || !this.lobbyFoot) {
      this.lobbyBody = h('div.lobby-body');
      this.lobbyFoot = h('div.lobby-foot');
      const social = h('div.lobby-social');
      // The chat first, in sight without scrolling: it is how the group gathers.
      if (this.chat) {
        social.append(h('div.lobby-sec.lobby-chat', {}, h('h4.lobby-label', {}, t('chat.title')), this.chat.el));
        this.chat.dock(null);
      }
      const carry = this.o.liveInfo?.carry;
      // Open where there is room; on a phone folded under the chat, one tap away.
      if (carry) social.append(h('details.lobby-sec.lobby-carry', { open: !window.matchMedia('(max-width: 640px)').matches }, h('summary.lobby-label', {}, t('carry.title')), carry));
      const allies = this.o.liveInfo?.allies;
      if (allies) social.append(h('div.lobby-sec.lobby-allies', {}, h('h4.lobby-label', {}, t('live.outside')), allies.el));
      this.lobbyEl = h('div.start-wrap.crypt-lobby.lobby', { 'data-test': 'lobby' }, this.lobbyBody, social, this.lobbyFoot);
      this.hud.append(this.lobbyEl);
      this.hud.classList.add('lobbying');
    }
    // Who is already in this room shows in the list, not again among the rest of the group.
    this.o.liveInfo?.allies?.skip?.(players);
    clear(this.lobbyBody);
    clear(this.lobbyFoot);
    return { body: this.lobbyBody, foot: this.lobbyFoot };
  }

  private partyList(players: string[], names: Record<string, string>, tag?: (pid: string) => HTMLElement | ''): HTMLElement {
    const s = this.o.state;
    const list = h('ul.crypt-party');
    for (const pid of players) {
      const hero = s.heroes.find((x) => x.pid === pid);
      list.append(h('li', {}, h('span', { html: ICONS.hero }), h('b', {}, names[pid] ?? pid), hero ? h('span.small', {}, t(`class.${hero.cls}`), ' · ', t('hero.level', { n: hero.level })) : '', tag?.(pid) ?? ''));
    }
    return h('div.lobby-sec', {}, h('h4.lobby-label', {}, t('party.title', { n: players.length })), list);
  }

  /** The fight starts: the card goes, the chat folds into its button over the field. */
  private closeLobby(): void {
    this.lobbyEl?.remove();
    this.lobbyEl = this.lobbyBody = this.lobbyFoot = null;
    this.hud.classList.remove('lobbying');
    this.chat?.dock(this.hud);
    this.dropAllies();
  }

  private dropAllies(): void {
    this.o.liveInfo?.allies?.el.remove();
  }
  private lobbyClock: HTMLElement | null = null;
  private countEl: HTMLElement | null = null;
  private lastCount = -1;

  /**
   * The wait for the wave: a big clock in the room that turns red and beats in the last minute, then
   * the last ten seconds one by one in the middle of the screen, each with a drum.
   */
  private countdown(left: number): void {
    const secs = Math.ceil(left / 1000);
    if (this.lobbyClock) {
      this.lobbyClock.textContent = `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;
      this.lobbyClock.parentElement?.classList.toggle('final', secs <= 60);
    }
    // The last seconds own the screen: the room's panel steps back.
    this.lobbyEl?.classList.toggle('counting', secs <= 10);
    if (secs > 10 || secs <= 0) {
      if (secs <= 0 && this.countEl) {
        this.countEl.remove();
        this.countEl = null;
      }
      return;
    }
    if (secs === this.lastCount) return;
    this.lastCount = secs;
    if (!this.countEl) {
      this.countEl = h('div.big-count', { 'aria-live': 'assertive' });
      this.hud.append(this.countEl);
    }
    // A fresh node per second restarts the pop animation.
    clear(this.countEl);
    this.countEl.append(h('span', { class: secs <= 3 ? 'last' : '' }, String(secs)));
    // The last three hit the whole screen.
    this.countEl.classList.remove('shake');
    if (secs <= 3) {
      void this.countEl.offsetWidth;
      this.countEl.classList.add('shake');
    }
    audio.play('drum', secs <= 3 ? 1 : 0.7);
  }
  private lobbyKey = '';

  /** The crypt's lobby: who is here, how many are missing, and the way down once there are enough. */
  private cryptLobby(players: string[], names: Record<string, string>): void {
    const s = this.o.state;
    if (s.started) {
      if (this.turretHintDue) this.turretHint();
      if (this.lobbyEl) {
        this.closeLobby();
        audio.play('horn');
        this.flashBanner(this.o.title, t('dungeon.descend', { names: players.map((p) => names[p] ?? p).join(', ') }));
      }
      return;
    }
    const key = players.join(',');
    if (this.lobbyEl && key === this.lobbyKey) return;
    this.lobbyKey = key;
    const { body, foot } = this.lobbyCard(players);
    const n = players.length;
    const claimed = s.mode === 'dungeon' ? (this.o.liveInfo?.claimed?.() ?? []) : [];
    // The garrison cellars go down with three to eight, all connected.
    const exp = !!s.dungeon?.exp;
    // The Furrows go down with one to eight.
    const maze = !!s.dungeon?.maze;
    const list = this.partyList(players, names, (pid) => (claimed.includes(pid) ? h('span.small.claimed', { title: t(exp ? 'exp.claimedTip' : maze ? 'maze.factPaid' : 'dungeon.claimedTip') }, t('dungeon.claimedTag')) : ''));
    const min = exp ? CELLARS.minHeroes : maze ? MAZE.minHeroes : (this.o.liveInfo?.minParty ?? DUNGEON.minLive);
    const go = h('button.btn', { disabled: n < min, 'data-test': 'crypt-start', onclick: () => this.send({ t: 'start' }) }, exp ? t('exp.enter') : maze ? t('maze.enter') : s.mode === 'dungeon' ? t('dungeon.enter') : t('party.go'));
    const crypt = s.mode === 'dungeon';
    const max = exp ? CELLARS.maxHeroes : maze ? MAZE.maxHeroes : crypt ? DUNGEON.maxHeroes : 1 + INCURSION.maxCompanions;
    const sub = exp
      ? n < min
        ? t(min - n === 1 ? 'exp.needOne' : 'exp.needMany', { n: min - n, min })
        : t('exp.ready', { n, max })
      : n < min
        ? t(crypt ? 'dungeon.need' : 'party.need', { n: min - n })
        : t('dungeon.ready', { n, max });
    body.append(h('div.lobby-head', {}, h('div', {}, h('h3', {}, exp ? t('exp.lobby') : maze ? t('maze.lobby') : crypt ? t('dungeon.lobby') : t('party.lobby')), h('p.lobby-sub', {}, sub))), list);
    const hint = exp ? t('exp.lobbyHint', { min, max, s: CELLARS.dropGraceTicks / 12 }) : maze ? t('maze.lobbyHint', { max, m: MAZE.torchTicks / 720 }) : crypt ? t('dungeon.lobbyHint') : t('party.lobbyHint');
    foot.append(h('p', {}, hint), h('div.lobby-actions', {}, h('button.btn.small.ghost', { 'data-test': 'lobby-leave', onclick: () => this.o.onExit() }, t('party.leave')), go));
  }

  /** The one who opened the room left before setting out: say so and offer the way back. */
  private lobbyCancelled(): void {
    if (!this.lobbyEl) return;
    const { body, foot } = this.lobbyCard([]);
    this.lobbyEl.classList.add('cancelled');
    body.append(h('div.lobby-head', {}, h('div', {}, h('h3', {}, t('party.cancelled')), h('p.lobby-sub', {}, t('party.cancelledSub')))));
    foot.append(h('div.lobby-actions', {}, h('button.btn.small', { 'data-test': 'lobby-back', onclick: () => this.o.onExit() }, t('party.back'))));
  }

  /** The leave button or V: home with what is on the cart, if everyone still standing is by it (else say so). */
  private leave(): void {
    if (!this.leaveReady(this.o.state)) {
      this.toast(t(this.o.state.rules?.hunt ? 'side.hunt.leaveFar' : 'plunder.leaveFar'), 2200);
      return;
    }
    this.send({ t: 'leave' });
    audio.play('click', 0.6);
  }

  /** Ready to go home: a plunder's cart, or the hunt's camp (everyone still up by the node). */
  private leaveReady(s: BattleState): boolean {
    const r = s.rules?.hunt;
    if (!r) return plunderLeaveReady(s);
    const up = s.heroes.filter((x) => x.downTicks === 0);
    return up.length > 0 && up.every((x) => x.x * x.x + x.y * x.y <= r.camp * r.camp);
  }

  /** The hunt: the traps this hero may still lay this outing (and none past the field's limit). */
  private trapsLeft(s: BattleState): number {
    const r = s.rules?.hunt;
    const me = this.me();
    if (!r || !me || !s.hunt) return 0;
    if (s.hunt.traps.length >= r.trap.max) return 0;
    return Math.max(0, r.trap.per - s.hunt.traps.filter((x) => !x.old && x.pid === me.pid).length);
  }

  /** The hammer button or C: strike the Anvil (the sim decides whether it was on the beat). */
  private strike(): void {
    const s = this.o.state;
    const me = s.heroes.find((x) => x.pid === this.o.me);
    const r = s.rules?.anvil ?? s.rules?.toll;
    if (!me || !r || (me.x ** 2 + me.y ** 2) > r.reach ** 2) {
      this.toast(t(s.rules?.toll ? 'side.toll.far' : 'side.anvil.far'), 1800);
      return;
    }
    this.send({ t: 'strike' });
  }

  /** The trap button or C: one at the hero's feet. */
  private trap(): void {
    if (this.trapsLeft(this.o.state) <= 0) {
      this.toast(t('side.hunt.noTraps'), 2200);
      return;
    }
    this.send({ t: 'trap' });
    audio.play('click', 0.6);
  }

  /** Plunder tracker: what is on the cart (against an extraction's worth), the wave and what is on the field. */
  private updatePlunder(s: BattleState, field: number): void {
    const p = s.plunder;
    if (!p) return;
    const cap = Math.trunc((PLUNDER.fullLoad * PLUNDER.capBp) / 10000);
    const left = Math.max(0, Math.ceil((PLUNDER.maxTicks - s.tick) / 12));
    setText(this.golemEl, `${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}`);
    setText(this.waveNum, String(p.loaded));
    // La Quemadura's camp: what counts is having enough to leave with, not the pay.
    const saga = s.rules?.place === 'camp';
    setText(this.waveLabel, saga ? t('side.campLoaded', { n: PLUNDER.winLoad }) : t('plunder.loaded', { n: PLUNDER.fullLoad }));
    setFill(this.waveFill, Math.min(100, (100 * p.loaded) / (saga ? PLUNDER.winLoad : cap)) / 100);
    const wave = Math.max(1, Math.floor((s.waveT - PLUNDER.firstWave) / PLUNDER.waveEvery) + 1);
    const next = PLUNDER.waveEvery - ((s.waveT - PLUNDER.firstWave) % PLUNDER.waveEvery);
    const stolen = p.caches.filter((c) => c.dragger).length;
    const sub = s.result !== 'running'
      ? t(p.end === 'fallen' ? 'plunder.fallen' : 'plunder.home')
      : [t('plunder.wave', { w: wave, s: Math.ceil(next / 12) }), saga ? '' : plunderLate(s) ? t('plunder.lateOn', { n: PLUNDER.lateBonus }) : t('plunder.late', { w: PLUNDER.lateWave, n: PLUNDER.lateBonus }), field ? t('hud.onField', { n: field }) : '', stolen ? t('plunder.stolen', { n: stolen }) : '', p.caches.length ? t('plunder.left', { n: p.caches.length }) : t('plunder.none')].filter(Boolean).join(' · ');
    setText(this.waveText, sub);
    this.waveTrack.classList.toggle('last', stolen > 0);
    this.waveTrack.classList.toggle('done', s.result === 'won');
  }

  /** Incursion tracker: "38 / 60 wood", the extraction bar, and which wave or the fast pace. */
  private updateExtraction(o: NonNullable<BattleState['objective']>, field: number, incoming: number): void {
    const s = this.o.state;
    const [res, full] = (this.o.extraction?.loot[0] ?? ['stone', 0]) as [string, number];
    const k = o.progress / o.total;
    const won = s.result === 'won';
    setText(this.waveNum, `${Math.floor(full * k)}`);
    setText(this.waveLabel, won ? t('inc.extractedAll') : t('inc.extracting', { n: full, res: t(`res.${res}`).toLowerCase() }));
    setFill(this.waveFill, (100 * k) / 100);
    const third = Math.trunc(INCURSION.extractTicks / INCURSION.waves);
    const wave = Math.min(INCURSION.waves, Math.max(1, Math.floor((s.waveT - 60) / third) + 1));
    let sub: string;
    const waiting = !!s.rules?.clearToFinish && o.progress >= o.total - 1 && field > 0;
    if (won) sub = t('inc.done');
    else if (waiting) sub = t('inc.finishThem', { n: field });
    else if (o.quiet) sub = t('inc.quiet', { n: o.quietRate ?? 1 });
    else sub = [t('inc.waveOf', { w: wave, n: INCURSION.waves }), field ? t('hud.onField', { n: field }) : '', incoming ? t('hud.incoming', { n: incoming }) : ''].filter(Boolean).join(' · ');
    setText(this.waveText, sub);
    this.waveTrack.classList.toggle('quiet', !!o.quiet && !won);
    this.waveTrack.classList.toggle('last', waiting);
    this.waveTrack.classList.toggle('done', won);
  }

  /** A new enemy kind or room since the last tick: kept for the device report's hitches (a first drawing compiles). */
  private noteNew(): void {
    const st = this.o.state;
    for (const e of st.enemies) {
      if (this.kindsSeen.has(e.kind)) continue;
      this.kindsSeen.add(e.kind);
      this.lastNew = { at: performance.now(), what: `+${e.kind}` };
    }
    const room = st.dungeon?.room ?? -1;
    if (room !== this.lastRoom) {
      this.lastRoom = room;
      this.lastNew = { at: performance.now(), what: `room${room}` };
    }
  }

  private updateHud(): void {
    this.noteNew();
    this.updateLobby();
    this.dg?.update();
    this.ensureParty();
    this.party?.update(this.o.state);
    // Signals once the fight is on and someone else plays it live.
    if (this.signals) {
      let live = 0;
      for (const x of this.o.state.heroes) if (x.control === 'live') live++;
      this.signals.setVisible(this.o.state.started !== false && this.o.state.result === 'running' && live >= 2);
    }
    const choosing = this.aiming >= 0 && this.aiming === this.allySlot();
    this.party?.choosing(choosing);
    this.dg?.party.choosing(choosing);
    const s = this.o.state;
    const cart = s.escort ? s.bots?.find((b) => b.id === s.escort?.cart) : undefined;
    const k = cart ? { hp: cart.hp, maxHp: cart.maxHp } : (s.objective ?? s.keep);
    setFill(this.keepBar, ((100 * Math.max(0, k.hp)) / k.maxHp) / 100);
    // The bell's climb: its life is its ropes.
    if (cart && s.rules?.climb) setText(this.keepText, t('side.climb.ropes', { n: s.climb?.ropes ?? s.rules.climb.ropes, of: s.rules.climb.ropes }));
    else setText(this.keepText, `${Math.max(0, k.hp)} / ${k.maxHp}`);
    if (s.escort) setFill(this.extractBar, ((100 * Math.min(s.escort.next, s.escort.road.length)) / s.escort.road.length) / 100);
    else if (s.rules?.bossWins || this.nightBoss) {
      const b = s.enemies.find((e) => e.id === s.bossId);
      setFill(this.extractBar, b ? Math.max(0, b.hp) / b.maxHp : s.bossId ? 0 : 1);
    }
    const field = s.enemies.filter((e) => e.hp > 0 && e.kind !== 'boneWall').length;
    const incoming = s.wave.spawns.length - s.wave.next;
    const left = field + incoming;
    // Splits and summons add enemies beyond the plan: the total grows with them so the bar never runs backwards.
    this.waveTotal = Math.max(this.waveTotal, s.wave.spawns.length, left + this.waveKills(s));
    const total = Math.max(1, this.waveTotal);
    const done = s.result === 'won' || (s.started && left === 0);
    setText(this.waveNum, done ? '✓' : String(left));
    setText(this.waveLabel, done ? t('hud.cleared') : !s.started ? t('hud.waveBrings', { n: left }) : t(left === 1 ? 'hud.leftOne' : 'hud.leftMany'));
    setFill(this.waveFill, (s.started ? (100 * (total - left)) / total : 0) / 100);
    setText(this.waveText, [s.started && field ? t('hud.onField', { n: field }) : '', s.started && incoming ? t('hud.incoming', { n: incoming }) : '', s.started ? fmtTime(s.waveT) : '']
      .filter(Boolean)
      .join(' · '));
    this.waveTrack.classList.toggle('last', s.started && incoming === 0 && left > 0 && left <= 3);
    this.waveTrack.classList.toggle('done', done);
    if (s.plunder) this.updatePlunder(s, field);
    else if (this.o.extraction && s.objective) this.updateExtraction(s.objective, field, incoming);
    if (s.objective && !s.rules?.bossWins) setFill(this.extractBar, ((100 * s.objective.progress) / s.objective.total) / 100);
    if (s.rules?.rescue) {
      // The pit in the woods: the track counts who is home, and where the rest are.
      const r = s.rules.rescue;
      const st = s.rescue;
      setText(this.waveNum, String(st?.saved ?? 0));
      setText(this.waveLabel, t('side.rescueOf', { n: r.people }));
      setFill(this.waveFill, ((100 * (st?.saved ?? 0)) / r.people) / 100);
      setText(this.waveText, t('side.rescueWhere', { pit: st?.left ?? r.people, with: st?.people.length ?? 0 }));
      // Everyone out of the pit: the rest is bringing them home.
      if (this.rescueRule) setText(this.rescueRule, (st?.left ?? r.people) > 0 ? t('side.rescueRule') : t('side.rescueHome'));
      this.waveTrack.classList.remove('last', 'done');
    }
    if (s.rules?.herd) {
      // The drive: the track counts the boars in the pen against the ones asked for, and how many are still out.
      const r = s.rules.herd;
      const st = s.herd;
      const penned = st?.penned ?? 0;
      const out = (st?.boars.length ?? r.boars) - penned;
      setText(this.waveNum, String(penned));
      setText(this.waveLabel, t('side.herdOf', { n: r.need }));
      setFill(this.waveFill, (Math.min(100, (100 * penned) / r.need)) / 100);
      setText(this.waveText, [t('side.herdOut', { n: out }), st?.lost ? t('side.herdLost', { n: st.lost }) : ''].filter(Boolean).join(' · '));
      this.waveTrack.classList.remove('last');
      this.waveTrack.classList.toggle('done', penned >= r.need);
    }
    if (s.rules?.blast) {
      // The blasting: the track counts the cracks sealed, and when the next barrel comes down.
      const r = s.rules.blast;
      const st = s.blast;
      const sealed = st?.sealed.filter(Boolean).length ?? 0;
      const waiting = !!st?.barrels.some((b) => !b.pid && b.crack < 0);
      const next = Math.max(0, Math.ceil(((st?.next ?? r.first) - s.waveT) / 12));
      setText(this.waveNum, String(sealed));
      setText(this.waveLabel, t('side.blastOf', { n: r.cracks.length }));
      setFill(this.waveFill, sealed / r.cracks.length);
      setText(this.waveText, st?.lit ? t('side.blast.beacon') : waiting ? t('side.blastReady') : t('side.blastNext', { s: next }));
      this.waveTrack.classList.remove('last');
      this.waveTrack.classList.toggle('done', !!st?.lit);
    }
    if (s.rules?.anvil) {
      // The five strikes: the track counts the strikes on the beat, and says whether the Anvil beats or rests.
      const r = s.rules.anvil;
      const st = s.anvil;
      const n = st?.strikes ?? 0;
      const rest = Math.max(0, Math.ceil(((st?.open ?? 0) - s.waveT) / 12));
      setText(this.waveNum, String(n));
      setText(this.waveLabel, t('side.anvilOf', { n: r.need }));
      setFill(this.waveFill, n / r.need);
      setText(this.waveText, st?.done ? t('side.anvil.fifth') : rest > 0 ? t('side.anvilRest', { s: rest }) : t('side.anvilNow'));
      this.waveTrack.classList.remove('last');
      this.waveTrack.classList.toggle('hot', !st?.done && rest === 0);
      // A count-in to keep time by: two soft drums a second apart, and the bell as it closes.
      const d = st ? st.close - s.waveT : -1;
      if (st && !st.done && s.waveT >= st.open && (d === 24 || d === 12 || d === 0) && d !== this.anvilBeat) audio.play(d === 0 ? 'bell' : 'drum', d === 0 ? 0.7 : 0.45);
      this.anvilBeat = d;
      this.waveTrack.classList.toggle('done', !!st?.done);
    }
    if (s.rules?.toll) {
      // The bell at midnight: the track is the calm, and says when to strike; a count-in to keep time by.
      const r = s.rules.toll;
      const st = s.toll;
      const n = st?.calm ?? 0;
      setText(this.waveNum, String(n));
      setText(this.waveLabel, t('side.tollOf', { n: r.need }));
      setFill(this.waveFill, n / r.need);
      const d = st ? st.close - s.waveT : -1;
      const hot = !!st && !st.done && d >= -r.window && d <= 12;
      // The rule line already says what to do: the track only calls the moment.
      setText(this.waveText, st?.done ? t('side.toll.calmFull') : hot ? t('side.tollNow') : '');
      this.waveTrack.classList.remove('last');
      this.waveTrack.classList.toggle('hot', hot);
      if (st && !st.done && s.started && (d === 24 || d === 12) && d !== this.anvilBeat) audio.play('drum', 0.45);
      this.anvilBeat = d;
      this.waveTrack.classList.toggle('done', !!st?.done);
    }
    if (s.rules?.maw) {
      // The Maw: the braziers lit, its life, and whether its light drags or it is dark.
      const r = s.rules.maw;
      const st = s.maw;
      const m = st ? s.enemies.find((e) => e.id === st.id) : undefined;
      const lit = st?.lit.filter(Boolean).length ?? r.braziers.length;
      const dark = Math.max(0, Math.ceil(((st?.dark ?? 0) - s.tick) / 12));
      setText(this.waveNum, String(lit));
      setText(this.waveLabel, t('side.mawOf', { n: r.braziers.length }));
      setFill(this.waveFill, m ? Math.max(0, m.hp) / Math.max(1, m.maxHp) : 0);
      setText(this.waveText, st?.done ? t('side.maw.felled') : st?.dark ? t('side.mawDark', { s: dark }) : t('side.mawLit'));
      this.waveTrack.classList.remove('last');
      this.waveTrack.classList.toggle('hot', !!st?.dark);
      this.waveTrack.classList.toggle('done', !!st?.done);
    }
    if (s.rules?.dragon) this.updateDragon(s);
    if (s.rules?.fin) {
      // The fin: when it comes down next (or which lane it marked), and the time till dawn.
      const r = s.rules.fin;
      const st = s.fin;
      const fin = Math.max(0, Math.ceil(((st?.next ?? r.first) - s.waveT) / 12));
      const marked = (st?.lane ?? -1) >= 0;
      const dawn = Math.max(0, Math.ceil((r.dawn - s.waveT) / 12));
      setText(this.waveNum, '');
      setText(this.waveLabel, r.breath ? t(marked ? 'side.breathMarked' : 'side.breathNext', { s: fin }) : marked ? t('side.finMarked', { s: fin }) : t('side.finNext', { s: fin }));
      // La Orilla 5's last fight: no dawn, the dragon's life instead.
      const boss = s.rules.sunken?.boss;
      const d = boss && s.sunken?.id ? s.enemies.find((x) => x.id === s.sunken?.id) : undefined;
      setFill(this.waveFill, boss ? (d ? Math.max(0, d.hp) / Math.max(1, d.maxHp) : s.sunken?.id ? 0 : 1) : Math.min(1, s.waveT / r.dawn));
      setText(this.waveText, boss ? t(s.sunken?.done ? 'side.sunken.felled' : 'side.sunkenLife', { n: d ? Math.max(0, Math.ceil((d.hp * 100) / Math.max(1, d.maxHp))) : 100 }) : t(s.waveT >= r.night || r.breath ? 'side.finNight' : 'side.finDay', { s: dawn }));
      this.waveTrack.classList.remove('last');
      this.waveTrack.classList.toggle('hot', marked);
      this.waveTrack.classList.toggle('done', !!st?.done);
    }
    if (this.neckRule) {
      setText(this.neckRule, neckLine(s));
      this.neckRule.classList.toggle('hot', !!s.neck?.warned);
    }
    if (this.climbRule) setText(this.climbRule, climbLine(s));
    if (s.rules?.winch) {
      // Hanging the bell: the track is how far up it is wound; how many winches turn (none: it sinks).
      const r = s.rules.winch;
      const on = s.winch?.turning.filter(Boolean).length ?? 0;
      setText(this.waveNum, '');
      setText(this.waveLabel, t('side.hang.label'));
      setFill(this.waveFill, Math.min(1, (s.winch?.lift ?? 0) / Math.max(1, r.need)));
      setText(this.waveText, s.winch?.done ? t('side.hang.up') : on ? t('side.hang.turning', { n: on, of: r.at.length }) : t('side.hang.none'));
      this.waveTrack.classList.remove('last');
      this.waveTrack.classList.toggle('hot', !on && !s.winch?.done);
      this.waveTrack.classList.toggle('done', !!s.winch?.done);
    }
    if (s.rules?.tide) {
      // The low water: where the net is; the time till the water comes back, then till dawn.
      const r = s.rules.tide;
      const st = s.tide;
      const net = st?.net.at ?? 'reef';
      const back = !!st?.back;
      const left = Math.max(0, Math.ceil(((back ? r.dawn : r.low) - s.waveT) / 12));
      setText(this.waveNum, '');
      setText(this.waveLabel, t(`side.tideNet.${net}`));
      setFill(this.waveFill, net === 'reef' ? Math.min(1, (st?.net.held ?? 0) / r.unhook) : Math.min(1, s.waveT / r.dawn));
      setText(this.waveText, t(back ? 'side.tideDawn' : 'side.tideLow', { s: left }));
      this.waveTrack.classList.remove('last');
      this.waveTrack.classList.toggle('hot', back && net !== 'home');
      this.waveTrack.classList.toggle('done', !!st?.done);
    }
    if (s.rules?.fair) {
      // Fair day: the track counts the wares back at their stalls, and fills as the fair's time runs.
      const r = s.rules.fair;
      const { saved, of } = fairSaved(s);
      const left = Math.max(0, Math.ceil((r.out - s.waveT) / 12));
      setText(this.waveNum, String(saved));
      setText(this.waveLabel, t('side.fairOf', { n: of }));
      setFill(this.waveFill, Math.min(1, s.waveT / r.out));
      setText(this.waveText, s.fair?.done ? t('side.fair.end') : t('side.fairLeft', { s: left }));
      this.waveTrack.classList.remove('last');
      this.waveTrack.classList.toggle('done', !!s.fair?.done);
    }
    if (s.rules?.well) {
      // The ink in the water: the track counts the houses still standing, and fills as the mill's stone rolls to the well.
      const r = s.rules.well;
      const st = s.well;
      const left = r.houses.length - wellBurned(s);
      const wait = Math.max(0, Math.ceil((r.stoneAt - s.waveT) / 12));
      setText(this.waveNum, String(left));
      setText(this.waveLabel, t('side.wellOf', { n: r.lose }));
      setFill(this.waveFill, wellStoneDone(s) / 100);
      setText(this.waveText, st?.sealed ? t('side.well.seal') : st?.stone ? t('side.wellPush', { p: wellStoneDone(s) }) : t('side.wellWait', { s: wait }));
      this.waveTrack.classList.remove('last');
      this.waveTrack.classList.toggle('done', !!st?.sealed);
    }
    if (s.rules?.bells) {
      // The alarm bells: the track counts the levers down, and fills with the pull of the one being pulled.
      const r = s.rules.bells;
      const st = s.bells;
      const down = st?.down.filter(Boolean).length ?? 0;
      const pull = Math.max(0, ...(st?.held ?? []).map((n, i) => (st?.down[i] ? 0 : n)));
      setText(this.waveNum, String(down));
      setText(this.waveLabel, t('side.bellsOf', { n: r.rooms.length }));
      setFill(this.waveFill, Math.min(1, (down + Math.min(1, pull / r.pull)) / r.rooms.length));
      setText(this.waveText, st?.done ? t('side.bells.all') : pull > 0 ? t('side.bells.pull') : '');
      this.waveTrack.classList.remove('last');
      this.waveTrack.classList.toggle('done', !!st?.done);
    }
    if (s.rules?.trap) {
      // The old trap: the track shows the winding (both winches) and, wound, that it waits for the lever.
      const r = s.rules.trap;
      const st = s.trap;
      const wound = st ? st.wound.reduce((a, n) => a + Math.min(n, r.wind), 0) : 0;
      const full = r.wind * r.winches.length;
      const ready = !!st?.armed;
      setText(this.waveNum, String(st?.hits ?? 0));
      setText(this.waveLabel, t('side.trapHits'));
      setFill(this.waveFill, ready || st?.fireAt ? 1 : wound / full);
      setText(this.waveText, st?.fireAt ? t('side.trapFlying') : ready ? t('side.trapReady') : t('side.trapWinding', { n: Math.round((100 * wound) / full) }));
      this.waveTrack.classList.toggle('last', ready);
      this.waveTrack.classList.remove('done');
    }
    if (s.rules?.collapse) {
      // The collapse: the track counts the scouts on the rock against the ones asked for, and how many are still out.
      const r = s.rules.collapse;
      const st = s.collapse;
      const saved = st?.saved ?? 0;
      const out = (st?.people.length ?? r.spots.length) - saved - (st?.lost ?? 0);
      setText(this.waveNum, String(saved));
      setText(this.waveLabel, t('side.collapseOf', { n: r.need }));
      setFill(this.waveFill, Math.min(100, (100 * saved) / r.need) / 100);
      setText(this.waveText, [t('side.collapseOut', { n: out }), st?.lost ? t('side.collapseLost', { n: st.lost }) : ''].filter(Boolean).join(' · '));
      this.waveTrack.classList.remove('last');
      this.waveTrack.classList.toggle('done', saved >= r.need);
    }
    if (s.rules?.lash && this.lashEl) {
      // The lash: the time to the next tilt (a shout while it creaks), and how many went into the sea.
      const r = s.rules.lash;
      const st = s.lash;
      const n = st?.n ?? 0;
      const soon = st && (st.warn > 0 || st.slide > 0);
      const when = soon ? t('side.lashNow') : n >= r.total ? t('side.lashCalm') : t('side.lashIn', { n: Math.max(0, Math.ceil(((st?.next ?? r.first) - s.waveT) / 12)) });
      setText(this.lashEl, [when, st?.fell ? t('side.lashFell', { n: st.fell }) : ''].filter(Boolean).join(' · '));
      this.lashEl.classList.toggle('bet', !!soon);
    }
    if (s.rules?.crowd) {
      // The night of lanterns: the track counts the faces torn off, and how many have crossed; the nightmares don't matter here.
      const r = s.rules.crowd;
      const crossed = (s.crowd?.spawned ?? 0) - (s.crowd?.people.length ?? 0);
      setText(this.waveNum, String(s.crowd?.unmasked.length ?? 0));
      setText(this.waveLabel, t('side.crowdOf', { n: r.impostors.length }));
      setFill(this.waveFill, (Math.min(100, (100 * crossed) / r.people)) / 100);
      setText(this.waveText, t('side.crowdCrossed', { n: crossed, of: r.people }));
      this.waveTrack.classList.remove('last', 'done');
    }
    if (s.rules?.burst) {
      // A hundred at a blow: the track counts what fell in the last ten seconds (the sim trims them at the next kill),
      // against the hundred, with the best so far and the time left; what's left of the field doesn't matter here.
      const w = s.rules.burst;
      const now = (s.burst?.kills ?? []).filter((k) => k > s.tick - w.ticks).length;
      setText(this.waveNum, String(now));
      setText(this.waveLabel, t('bet.hud.of', { n: w.count }));
      setFill(this.waveFill, (Math.min(100, (100 * now) / w.count)) / 100);
      const left = s.objective ? Math.max(0, s.objective.total - s.objective.progress) : 0;
      setText(this.waveText, [s.burst?.best ? t('bet.hud.best', { n: s.burst.best }) : '', t('bet.hud.left', { t: fmtTime(left) })].filter(Boolean).join(' · '));
      this.waveTrack.classList.toggle('hot', now >= w.count * 0.6);
      this.waveTrack.classList.remove('last');
    }
    if (this.trapBtn) {
      const n = this.trapsLeft(s);
      setText(this.trapBtn.querySelector('.tcount') as HTMLElement, String(n));
      this.trapBtn.classList.toggle('off', n <= 0);
    }
    if (s.rules?.copies) this.updateCopies(s);
    if (s.rules?.hunt && this.huntEl) {
      // The hunt: how much life it has left of its whole (its wounds from before, too), and the traps out.
      const u = s.enemies.find((e) => e.kind === 'uro');
      const r = s.rules.hunt;
      const hp = u ? Math.max(0, u.hp) : r.hp;
      const traps = s.hunt?.traps.length ?? r.traps.length;
      setText(this.huntEl, [t('side.huntLeft', { n: Math.ceil((100 * hp) / Math.max(1, r.max)) }), traps ? t('side.huntTraps', { n: traps }) : '', t('side.huntTime', { t: fmtTime(Math.max(0, r.out - s.waveT)) })].filter(Boolean).join(' · '));
    }
    if (this.leaveBtn) {
      const ready = this.leaveReady(s);
      this.leaveBtn.classList.toggle('off', !ready);
      this.leaveBtn.setAttribute('aria-disabled', ready ? 'false' : 'true');
    }
    if (this.carryBtn) {
      const act = s.plunder ? plunderCarryAction(s, this.me()) : carryAction(s, this.me());
      const label = act === 'drop' ? t('exp.drop') : t('exp.carry');
      setText(this.carryBtn.querySelector('.carry-lbl') as HTMLElement, label);
      this.carryBtn.setAttribute('aria-label', label);
      this.carryBtn.classList.toggle('off', act === null);
      this.carryBtn.classList.toggle('holding', act === 'drop');
      this.carryBtn.setAttribute('aria-disabled', act === null ? 'true' : 'false');
    }
    if (this.turretBtn) {
      const used = this.me()?.turretsPlaced ?? 0;
      const cd = this.turretBtn.querySelector('.tcount') as HTMLElement;
      const max = this.turretMax();
      setText(cd, used >= max ? t('inc.turretsOut') : String(max - used));
      this.turretBtn.classList.toggle('out', used >= max);
      if (used >= max && this.placingTurret) this.placingTurret = false;
      this.turretBtn.classList.toggle('aiming', this.placingTurret);
    }
    if (s.purse) {
      for (const key of ['wood', 'stone', 'crystal'] as const) {
        const el = this.resEls[key];
        if (el && el.textContent !== String(s.purse[key])) el.textContent = String(s.purse[key]);
      }
      for (const c of this.cards) {
        const cost = STRUCTURES[c.type].cost;
        const poor = s.purse.wood < cost.wood || s.purse.stone < cost.stone || s.purse.crystal < cost.crystal;
        c.el.classList.toggle('poor', poor);
        c.el.classList.toggle('active', this.building === c.type);
      }
    }
    const me = this.me();
    if (me) {
      setFill(this.heroBar, ((100 * Math.max(0, me.hp)) / me.stats.maxHp) / 100);
      // The countdown itself lives in the big card under the wave tracker.
      setText(this.heroText, me.downTicks > 0 ? t('hud.downShort') : `${me.hp} / ${me.stats.maxHp}`);
      if (me.downTicks > 0) {
        this.downMax = Math.max(this.downMax, me.downTicks);
        setText(this.downNum, t('hud.downIn', { n: Math.ceil(me.downTicks / 12) }));
        setFill(this.downFill, ((100 * (this.downMax - me.downTicks)) / Math.max(1, this.downMax)) / 100);
      } else this.downMax = 0;
      this.downEl.classList.toggle('show', me.downTicks > 0);
      (kitOf(me) as SkillId[]).forEach((id, i) => {
        const el = this.skillEls[i];
        if (!el) return;
        const cdTicks = me.skillCd[i] as number;
        const total = SKILLS[id].cooldown;
        const cd = el.querySelector('.cd') as HTMLElement;
        setStyle(cd, '--p', `${(100 * cdTicks) / total}%`);
        setText(cd, cdTicks > 0 ? String(Math.ceil(cdTicks / 12)) : '');
        el.classList.toggle('ready', cdTicks === 0 && me.downTicks === 0);
        el.classList.toggle('aiming', this.aiming === i);
      });
      if (this.overloadBtn) {
        // Grayed while nothing of its own can blow up; a tap then says why.
        const any = this.overloadTargets().length > 0;
        if (!any) this.overloading = false;
        const cdTicks = me.overloadCd ?? 0;
        const cd = this.overloadBtn.querySelector('.cd') as HTMLElement;
        setStyle(cd, '--p', `${(100 * cdTicks) / ARTIFICER.overload.cooldown}%`);
        setText(cd, cdTicks > 0 ? String(Math.ceil(cdTicks / 12)) : '');
        this.overloadBtn.classList.toggle('out', !any);
        this.overloadBtn.classList.toggle('ready', any && cdTicks === 0 && me.downTicks === 0);
        this.overloadBtn.classList.toggle('aiming', this.overloading);
      }
    }
  }

  private openPause(): void {
    this.loop.paused = true;
    audio.play('click');
    const back = h('div.modal-back');
    const close = (): void => {
      back.remove();
      this.loop.paused = false;
    };
    back.append(
      h(
        'div.modal',
        { role: 'dialog', 'aria-modal': 'true' },
        h('header', {}, h('h2', {}, t('hud.pause'))),
        h('div.body', {}, h('p', { style: 'margin:0;color:var(--text-soft);font-family:var(--font-serif)' }, isTouch() ? t('controls.mobile') : t('controls.pc'))),
        h(
          'footer',
          {},
          h('button.btn.ghost', { onclick: () => { back.remove(); this.o.onExit(); } }, t('hud.quit')),
          h('button.btn', { onclick: close }, t('hud.resume')),
        ),
      ),
    );
    this.el.append(back);
  }

  // ---------- input ----------

  private bindInput(): void {
    const c = this.canvas;
    const on = <K extends keyof HTMLElementEventMap>(target: EventTarget, ev: K, fn: (e: HTMLElementEventMap[K]) => void, opts?: AddEventListenerOptions): void => {
      target.addEventListener(ev, fn as EventListener, opts);
      this.disposers.push(() => target.removeEventListener(ev, fn as EventListener, opts));
    };
    on(c, 'pointerdown', (e) => this.pointerDown(e));
    on(window, 'pointermove', (e) => this.pointerMove(e));
    on(window, 'pointerup', (e) => this.pointerUp(e));
    on(window, 'pointercancel', (e) => this.pointerUp(e));
    on(c, 'wheel', (e) => {
      e.preventDefault();
      this.cam.zoom(Math.exp(e.deltaY * 0.0012));
    }, { passive: false });
    on(c, 'contextmenu', (e) => e.preventDefault());
    on(window, 'keydown', (e) => this.keyDown(e));
    on(window, 'keyup', (e) => this.keyUp(e));
    on(window, 'blur', () => {
      this.held = -1;
      if (this.signals?.aimingCursor) this.signals.cancel();
    });
    on(window, 'resize', () => this.resize());
    on(c, 'pointerleave', () => (this.mouse.inside = false));
  }

  private resize(): void {
    const w = viewW();
    const hh = viewH();
    const pr = Math.min(window.devicePixelRatio || 1, settings.quality === 'high' ? 2 : 1.5);
    this.o.renderer.setPixelRatio(pr);
    this.o.renderer.setSize(w, hh, false);
    this.cam.resize(w / hh);
    this.post.setSize(w, hh, pr);
    this.view.pixels = hh * pr;
  }

  private groundAt(x: number, y: number): THREE.Vector3 | null {
    const ndc = new THREE.Vector2((x / viewW()) * 2 - 1, -(y / viewH()) * 2 + 1);
    this.ray.setFromCamera(ndc, this.cam.camera);
    // On terraced land the ground stands at its own height: pick where the ray meets it.
    if (this.o.state.map.land) return this.view.groundHit(this.ray.ray);
    const hit = new THREE.Vector3();
    return this.ray.ray.intersectPlane(this.plane, hit) ? hit : null;
  }

  /** Enemy closest to a screen point within a pick radius. */
  private pickEnemy(x: number, y: number): number {
    const s = this.o.state;
    let best = 0;
    let bd = (isTouch() ? 44 : 30) ** 2;
    const v = new THREE.Vector3();
    for (const e of s.enemies) {
      if (e.hp <= 0 || !isRevealed(s, e)) continue;
      const p = this.view.positions.get(e.id);
      if (!p) continue;
      // Aim at the middle of the body as drawn (a warlord stands far taller than a goblin).
      const key = `enemy:${e.kind}:${e.tier}:${e.elite ? 1 : 0}`;
      const tall = this.view.lib.heightOf(key) * this.view.lib.scaleOf(key) * (e.elite && e.kind !== 'warden' ? 1.75 : 1);
      v.copy(p).setY(p.y + tall * 0.5).project(this.cam.camera);
      const sx = (v.x * 0.5 + 0.5) * viewW();
      const sy = (-v.y * 0.5 + 0.5) * viewH();
      const d = (sx - x) ** 2 + (sy - y) ** 2;
      if (d < bd) {
        bd = d;
        best = e.id;
      }
    }
    return best;
  }

  private pointerDown(e: PointerEvent): void {
    audio.unlock();
    // Alt + click: the obvious signal for what is under the mouse (a nightmare: attack; my hero: help; else: defend).
    if (e.altKey && e.pointerType === 'mouse' && e.button === 0 && this.signals && !this.signals.el.hidden) {
      e.preventDefault();
      this.signals.smart(e.clientX, e.clientY);
      return;
    }
    // G held: a click picks the lit petal (the same as letting G go).
    if (this.signals?.aimingCursor) {
      this.signals.releaseCursor();
      return;
    }
    // The wheel open by its button: a touch anywhere else shuts it.
    if (this.signals?.isOpen) {
      this.signals.cancel();
      return;
    }
    this.canvas.setPointerCapture?.(e.pointerId);
    this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY, sx: e.clientX, sy: e.clientY, t: performance.now(), button: e.button });
    window.clearTimeout(this.steerTimer);
    if (this.pointers.size === 2) {
      const [a, b] = [...this.pointers.values()] as unknown as [{ x: number; y: number }, { x: number; y: number }];
      this.gesture = { mode: 'pinch', dist: Math.hypot(a.x - b.x, a.y - b.y), angle: Math.atan2(b.y - a.y, b.x - a.x) };
    } else if (e.pointerType !== 'mouse' && (this.placingTurret || this.overloading || this.aiming >= 0 || this.signals?.pending)) {
      // Placing or aiming with a finger: it never pans. The spot follows the finger and lands where it lifts,
      // so a finger that slips in the rush of a fight still places instead of moving the camera.
      this.gesture = { mode: 'aim', dist: 0, angle: 0 };
      this.mouse = { x: e.clientX, y: e.clientY, inside: true };
    } else {
      this.gesture = { mode: 'none', dist: 0, angle: 0 };
      // Held still on the board: the hero starts following the finger. Not with a mouse or trackpad, where
      // a press that rests a moment before dragging means moving the camera (as in the bastion).
      if (e.pointerType !== 'mouse' && e.button === 0 && this.canSteer()) {
        const id = e.pointerId;
        this.steerTimer = window.setTimeout(() => {
          const p = this.pointers.get(id);
          if (!p || this.pointers.size !== 1 || this.gesture.mode !== 'none' || !this.canSteer()) return;
          this.gesture.mode = 'steer';
          this.steer(p.x, p.y, true, true);
        }, STEER_HOLD);
      }
    }
  }

  /**
   * Incursions and defenses with companions: who is with you, their health, who is down. Built once
   * there is more than one hero — in a live room friends arrive after the screen opens.
   */
  private ensureParty(): void {
    const s = this.o.state;
    if (this.party || s.mode === 'dungeon' || s.heroes.length < 2 || this.o.sandbox) return;
    this.party = new PartyFrames(this.o.me, this.o.liveInfo?.names ?? {}, 'party-hud');
    this.hud.append(this.party.el);
    // On touch the strip sits right under the wave's track, whose height changes with what it says.
    if (isTouch()) {
      const el = this.party.el;
      const place = (): void => el.style.setProperty('--pf-top', `${Math.max(this.waveTrack.offsetTop + this.waveTrack.offsetHeight, 96) + 6}px`);
      new ResizeObserver(place).observe(this.waveTrack);
      place();
    }
    // A cleric heals by touching a name in the party list.
    if (this.allySlot() >= 0) this.party.onPick = (id) => this.healAlly(id);
  }

  /** Steering only walks: not while building, aiming, placing a turret, or down. */
  private canSteer(): boolean {
    const me = this.me();
    return !!me && me.downTicks <= 0 && this.o.state.result === 'running' && !this.building && !this.placingTurret && !this.overloading && this.aiming < 0;
  }

  /** Whether a screen point is on your own hero (to grab it and drag). */
  private onMyHero(x: number, y: number): boolean {
    const me = this.me();
    const p = me ? this.view.positions.get(me.id) : undefined;
    if (!p) return false;
    const v = p.clone().setY(p.y + 0.5).project(this.cam.camera);
    return Math.hypot((v.x * 0.5 + 0.5) * viewW() - x, (-v.y * 0.5 + 0.5) * viewH() - y) < STEER_GRAB;
  }

  /** Sends the hero toward a screen point while steering, a few times a second at most. */
  private steer(x: number, y: number, force = false, start = false): void {
    const now = performance.now();
    if (!force && (now - this.steerSent.t < 120 || Math.hypot(x - this.steerSent.x, y - this.steerSent.y) < 8)) return;
    const g = this.groundAt(x, y);
    if (!g) return;
    const sp = worldToSim(g);
    this.send({ t: 'move', x: sp.x, y: sp.y });
    this.steerSent = { x, y, t: now };
    this.view.vfx.ring(g.setY(0.05), 0.06, 0.28, '#eceef2', 3, 0.6);
    if (start) {
      this.coach?.event('move');
      audio.play('click', 0.4);
    }
  }

  private pointerMove(e: PointerEvent): void {
    this.mouse.x = e.clientX;
    this.mouse.y = e.clientY;
    this.signals?.aim(e.clientX, e.clientY);
    this.mouse.inside = e.target === this.canvas;
    if (this.dragCard && e.pointerId === this.dragCard.pointerId) {
      this.dragCard.ghost.style.transform = `translate(${e.clientX - 30}px, ${e.clientY - 70}px)`;
      return;
    }
    const p = this.pointers.get(e.pointerId);
    if (!p) return;
    const dx = e.clientX - p.x;
    const dy = e.clientY - p.y;
    p.x = e.clientX;
    p.y = e.clientY;
    if (this.pointers.size >= 2) {
      const [a, b] = [...this.pointers.values()] as unknown as [{ x: number; y: number }, { x: number; y: number }];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const angle = Math.atan2(b.y - a.y, b.x - a.x);
      if (this.gesture.dist > 0) this.cam.zoom(this.gesture.dist / dist);
      let da = angle - this.gesture.angle;
      if (da > Math.PI) da -= Math.PI * 2;
      if (da < -Math.PI) da += Math.PI * 2;
      this.cam.rotate(da);
      this.gesture = { mode: 'pinch', dist, angle };
      return;
    }
    if (this.gesture.mode === 'aim') {
      this.mouse.inside = true;
      return;
    }
    const moved = Math.hypot(e.clientX - p.sx, e.clientY - p.sy);
    if (this.gesture.mode === 'none' && moved > DRAG_START && this.o.land && p.button === 0 && (this.erasing || this.building === 'wall' || this.building === 'spikes')) {
      // Terraced land: with the wall, the stakes or the eraser in hand, one finger draws; two still move the camera.
      this.gesture.mode = 'paint';
      this.painted = -1;
      this.paintAt(p.sx, p.sy);
    }
    if (this.gesture.mode === 'paint') {
      this.paintAt(e.clientX, e.clientY);
      return;
    }
    if (this.gesture.mode === 'none' && moved > DRAG_START) {
      window.clearTimeout(this.steerTimer);
      // A drag that starts on your own hero steers it; anywhere else it moves the camera.
      if (p.button === 0 && !e.shiftKey && this.canSteer() && this.onMyHero(p.sx, p.sy)) {
        this.gesture.mode = 'steer';
        this.steer(e.clientX, e.clientY, true, true);
      } else this.gesture.mode = p.button === 2 || e.shiftKey ? 'rotate' : 'pan';
    }
    if (this.gesture.mode === 'steer') {
      this.steer(e.clientX, e.clientY);
      return;
    }
    if (this.gesture.mode === 'rotate') this.cam.rotate(-dx * 0.006, dy * 0.004);
    else if (this.gesture.mode === 'pan') this.cam.pan(-dx * 0.03, dy * 0.03 * 1.3);
  }

  private pointerUp(e: PointerEvent): void {
    if (this.dragCard && e.pointerId === this.dragCard.pointerId) {
      this.finishCardDrag(e);
      return;
    }
    const p = this.pointers.get(e.pointerId);
    this.pointers.delete(e.pointerId);
    window.clearTimeout(this.steerTimer);
    if (!p) return;
    if (this.gesture.mode === 'steer') {
      // Letting go: one last step to where the finger was.
      if (e.type !== 'pointercancel') this.steer(e.clientX, e.clientY, true);
      if (this.pointers.size === 0) this.gesture = { mode: 'none', dist: 0, angle: 0 };
      return;
    }
    if (this.gesture.mode === 'paint') {
      if (this.pointers.size === 0) this.gesture = { mode: 'none', dist: 0, angle: 0 };
      this.painted = -1;
      return;
    }
    if (this.gesture.mode === 'aim') {
      if (this.pointers.size === 0) this.gesture = { mode: 'none', dist: 0, angle: 0 };
      this.mouse.inside = false;
      if (e.type !== 'pointercancel') this.tap(e.clientX, e.clientY);
      return;
    }
    const wasGesture = this.gesture.mode !== 'none';
    const pinched = this.gesture.mode === 'pinch';
    if (this.pointers.size === 0) this.gesture = { mode: 'none', dist: 0, angle: 0 };
    if (e.type === 'pointercancel' || pinched) return;
    // A quick click is a click even if the hand slipped a little in the rush of a fight.
    if (wasGesture && !quickClick(p, e)) return;
    if (p.button === 2) {
      // Right click: cancel a held skill, aiming, building or turret placing, otherwise move or attack.
      if (this.signals?.pending) {
        this.signals.cancel();
        return;
      }
      if (this.held >= 0 || this.aiming >= 0 || this.building || this.placingTurret || this.overloading) {
        this.held = -1;
        this.aiming = -1;
        this.building = null;
        this.placingTurret = false;
        this.overloading = false;
        return;
      }
    }
    this.tap(e.clientX, e.clientY);
  }

  /**
   * A click or tap on the board: builds, places and aims when one of those is under way; otherwise it
   * attacks the nightmare under the pointer or walks there. Left and right click both work.
   */
  private tap(x: number, y: number): void {
    // A signal picked from the wheel: this tap says where (off the board, it keeps waiting).
    if (this.signals?.pending) {
      this.signals.placeAt(x, y);
      return;
    }
    const g = this.groundAt(x, y);
    if (!g) return;
    const sp = worldToSim(g);
    if (this.erasing) {
      const hx = worldToHex(sp.x, sp.y);
      this.eraseCell(hx.q, hx.r);
      return;
    }
    if (this.building) {
      this.tryPlace(this.building, sp.x, sp.y);
      // On PC, build mode stays on for placing several in a row, except while the guide is talking.
      if (!isTouch() && !this.coach?.active) return;
      this.building = null;
      return;
    }
    if (this.placingTurret) {
      // Same rules as the sim, said out loud: near the hero, on open ground. Stay in placing mode.
      const me = this.me();
      if (!me) return;
      const dx = sp.x - me.x;
      const dy = sp.y - me.y;
      const range = SKILLS.turret.castRange;
      const d = Math.hypot(dx, dy);
      if (d > range * 1.6) {
        this.toast(t('inc.turretFar'), 2600);
        this.showTurretRange();
        return;
      }
      if (d > range) {
        // A little past the circle: pulled in to its edge (a finger is not a mouse).
        const k = (range * 0.96) / d;
        sp.x = Math.round(me.x + dx * k);
        sp.y = Math.round(me.y + dy * k);
      }
      if (!heroWalkable(this.o.state, cellAtWorld(this.o.state, sp.x, sp.y))) {
        this.toast(t('inc.turretBlocked'), 2200);
        return;
      }
      this.placingTurret = false;
      this.send({ t: 'turret', x: sp.x, y: sp.y });
      this.showTurretReach(sp.x, sp.y);
      return;
    }
    if (this.overloading) {
      // The turret nearest the tap; a finger a little off still takes it. Too far from all: stay picking.
      const target = this.overloadPick(sp.x, sp.y, isTouch() ? 1600 : 1000);
      if (!target) {
        this.toast(t('skill.overloadAim'), 2200);
        return;
      }
      this.overloading = false;
      this.send({ t: 'overload', x: target.x, y: target.y });
      return;
    }
    if (this.aiming >= 0) {
      const slot = this.aiming as 0 | 1 | 2;
      const turret = this.aimingTurret();
      // An ally skill (the cleric's mend, the paladin's aegis) takes the hero under the finger.
      const id = this.allySlot() === slot ? this.pickHero(x, y) : this.pickEnemy(x, y);
      const why = this.castAt(slot, sp.x, sp.y, id);
      // No open ground there: aiming stays on for another tap, as when placing a field turret.
      if (why === 'ground') {
        if (turret) this.showTurretRange();
        return;
      }
      this.aiming = -1;
      if (turret && !why) this.showTurretReach(sp.x, sp.y);
      return;
    }
    const enemy = this.pickEnemy(x, y);
    if (enemy) {
      this.send({ t: 'attack', id: enemy });
      const p = this.view.positions.get(enemy);
      if (p) this.view.vfx.ring(p, 0.2, 0.6, '#ff4f3a', 4, 1);
      return;
    }
    const cell = cellAtWorld(this.o.state, sp.x, sp.y);
    if (!heroWalkable(this.o.state, cell)) {
      // Clicked on a structure or off the walkable ground: walk next to it anyway.
    }
    this.send({ t: 'move', x: sp.x, y: sp.y });
    this.coach?.event('move');
    this.view.vfx.ring(g.setY(0.05), 0.1, 0.45, '#eceef2', 4, 0.9);
    audio.play('click', 0.6);
  }

  private keyDown(e: KeyboardEvent): void {
    if (e.repeat) return;
    // Typing in the chat: not a shortcut.
    if ((e.target as HTMLElement | null)?.closest?.('input, textarea')) return;
    const k = e.key.toLowerCase();
    // Enter opens the chat (Enter again sends it and folds it away).
    if (k === 'enter' && this.chat) {
      e.preventDefault();
      this.chat.focus();
      return;
    }
    if (k === 'q' || k === 'w' || k === 'e') {
      const slot = k === 'q' ? 0 : k === 'w' ? 1 : 2;
      // Held down, an aimed skill shows its reach and where it lands; it goes when the key is let go.
      if (this.aimable(slot)) this.held = slot;
      else this.useSkill(slot, false);
    } else if (k === 'g' && this.signals && !this.signals.el.hidden) {
      // Held: the signal wheel opens at the mouse; let go to send the lit one.
      if (this.mouse.inside) this.signals.openAtCursor(this.mouse.x, this.mouse.y);
    } else if (k === 'escape') {
      if (this.signals?.pending || this.signals?.aimingCursor) this.signals.cancel();
      else if (this.held >= 0) this.held = -1;
      else if (this.aiming >= 0 || this.building || this.overloading) {
        this.aiming = -1;
        this.building = null;
        this.overloading = false;
      } else if (this.o.allowPause) this.openPause();
    } else if (k === 'r') this.toggleTurret();
    else if (k === 'c' && this.carryBtn) this.carry();
    else if (k === 'c' && this.trapBtn) this.trap();
    else if (k === 'c' && this.strikeBtn) this.strike();
    else if (k === 'v' && this.leaveBtn) this.leave();
    else if (k === 'f') this.useOverload(false);
    else if (k === 's') this.send({ t: 'stop' });
    else if (k === ' ') {
      const me = this.me();
      if (me) this.cam.focus(simToWorld(me.x, me.y));
    } else if (k === 'arrowleft' || k === 'a') this.cam.rotate(0.3);
    else if (k === 'arrowright' || k === 'd') this.cam.rotate(-0.3);
  }

  private keyUp(e: KeyboardEvent): void {
    if (e.key.toLowerCase() === 'g' && this.signals?.aimingCursor) {
      this.signals.releaseCursor();
      return;
    }
    const slot = ['q', 'w', 'e'].indexOf(e.key.toLowerCase());
    if (slot < 0 || slot !== this.held) return;
    this.held = -1;
    this.useSkill(slot as 0 | 1 | 2, false);
  }

  /** A skill aimed at a point, a nightmare or an ally, ready, with the mouse over the board: it can be held to aim. */
  private aimable(slot: 0 | 1 | 2): boolean {
    const me = this.me();
    if (!me || me.downTicks > 0 || (me.skillCd[slot] as number) > 0 || !this.mouse.inside) return false;
    const sk = SKILLS[kitOf(me)[slot] as SkillId];
    return sk.targeting !== 'self' && sk.targeting !== 'structure' && sk.id !== 'shieldBash';
  }

  /**
   * PC aiming marks, every frame: for a held or aimed skill, its reach and where it lands (or whom it takes);
   * otherwise a ring under the nightmare the cursor is on, so a key press never goes somewhere unexpected.
   */
  private updateAim(): void {
    this.marks.hide();
    const me = this.me();
    const s = this.o.state;
    if (!me || me.downTicks > 0 || !this.mouse.inside || isTouch() || s.result !== 'running') return;
    const aimed = this.held >= 0 ? this.held : this.aiming;
    if (aimed < 0) {
      if (this.building || this.placingTurret) return;
      const id = this.pickEnemy(this.mouse.x, this.mouse.y);
      const p = id ? this.view.positions.get(id) : undefined;
      if (p) this.marks.showTarget(p);
      return;
    }
    const slot = aimed as 0 | 1 | 2;
    const at = this.view.positions.get(me.id);
    if (!at) return;
    const id = kitOf(me)[slot] as SkillId;
    const sk = rankedSkill(SKILLS[id], me.ranks?.[slot] ?? 1, me.forks?.[slot] ?? null, me.glyphs?.[slot] ?? null);
    this.marks.showReach(at, sk.castRange);
    if (sk.targeting === 'ally' || sk.targeting === 'guard') {
      const hid = this.pickHero(this.mouse.x, this.mouse.y);
      const p = hid ? this.view.positions.get(hid) : undefined;
      if (p) this.marks.showTarget(p, true);
      return;
    }
    const g = this.groundAt(this.mouse.x, this.mouse.y);
    if (!g) return;
    const sp = worldToSim(g);
    // The point, clamped to the reach as the simulation does.
    const dx = sp.x - me.x;
    const dy = sp.y - me.y;
    const d = Math.hypot(dx, dy);
    const k = d > sk.castRange ? sk.castRange / d : 1;
    const px = me.x + dx * k;
    const py = me.y + dy * k;
    if (sk.targeting === 'enemy') {
      // As the simulation picks (the net): the one under the cursor if in reach, else the nearest to the point.
      const near = (e: { x: number; y: number }, x: number, y: number, r: number): boolean => (e.x - x) ** 2 + (e.y - y) ** 2 <= r * r;
      let e = s.enemies.find((x) => x.id === this.pickEnemy(this.mouse.x, this.mouse.y));
      if (!e || !targetable(s, e) || !near(e, me.x, me.y, sk.castRange)) {
        e = undefined;
        let bd = Infinity;
        for (const x of s.enemies) {
          if (x.hp <= 0 || !targetable(s, x) || !near(x, px, py, 1500)) continue;
          const dd = (x.x - px) ** 2 + (x.y - py) ** 2;
          if (dd < bd) {
            bd = dd;
            e = x;
          }
        }
        if (e && !near(e, me.x, me.y, sk.castRange + 500)) e = undefined;
      }
      const p = e ? this.view.positions.get(e.id) : undefined;
      if (p) this.marks.showTarget(p);
      return;
    }
    // Where it lands; walls and turrets show a small mark.
    this.marks.showArea(px, py, at.y, sk.radius > 0 && id !== 'turret' ? sk.radius : 500);
  }

  /** Portable turrets in this incursion (older incursions: 3 for the whole run). */
  private turretMax(): number {
    return this.o.state.rules?.turrets?.max ?? 3;
  }

  /** Turret button or R: start (or cancel) placing a portable turret, if any are left. */
  /** The carry button or C: take what is at hand or put down what you hold (nothing at hand: say so). */
  private carry(): void {
    const act = this.o.state.plunder ? plunderCarryAction(this.o.state, this.me()) : carryAction(this.o.state, this.me());
    if (!act) {
      this.toast(t('exp.nothingNear'), 1800);
      return;
    }
    this.send({ t: 'carry' });
    audio.play('click', 0.6);
  }

  private toggleTurret(): void {
    if (!this.turretBtn) return;
    if ((this.me()?.turretsPlaced ?? 0) >= this.turretMax()) {
      this.placingTurret = false;
      this.toast(t('inc.turretsNone', { n: this.turretMax() }));
      return;
    }
    this.placingTurret = !this.placingTurret;
    this.overloading = false;
    if (this.placingTurret) {
      this.aimHint('turret', t('inc.turretAim'));
      this.showTurretRange();
    }
  }

  /** A ring around the hero: turrets go inside it. */
  private showTurretRange(): void {
    const me = this.me();
    if (!me) return;
    const c = simToWorld(me.x, me.y);
    const r = simToWorld(me.x + SKILLS.turret.castRange, me.y).distanceTo(c);
    this.view.vfx.ring(c, r * 0.97, r, '#ffb070', 30, 0.9);
  }

  /** Q/W/E or button press. Targeted skills smart-cast at the cursor on PC; on touch they enter aim mode. */
  private useSkill(slot: 0 | 1 | 2, fromButton: boolean): void {
    const me = this.me();
    if (!me || me.downTicks > 0) return;
    if ((me.skillCd[slot] as number) > 0) return;
    // Stunned, afraid, asleep or a bear: the sim would drop the cast, so say why instead of aiming for nothing.
    const held = castBlocked(this.o.state, me, slot, me.x, me.y);
    if (held && held !== 'ground') {
      this.aiming = -1;
      this.toast(t(`skill.no.${held}`), 2200);
      return;
    }
    const sk = SKILLS[kitOf(me)[slot] as SkillId];
    if (sk.targeting === 'self' || sk.targeting === 'structure') {
      this.send({ t: 'skill', slot });
      return;
    }
    if (sk.targeting === 'enemy' && sk.id === 'shieldBash') {
      this.send({ t: 'skill', slot });
      return;
    }
    if (sk.targeting === 'ally') {
      // Again while choosing: the most hurt. With the mouse over a hero (PC key): that hero.
      if (this.aiming === slot) {
        this.aiming = -1;
        this.send({ t: 'skill', slot });
        return;
      }
      if (!fromButton && this.mouse.inside) {
        this.castAt(slot, me.x, me.y, this.pickHero(this.mouse.x, this.mouse.y));
        return;
      }
      this.aiming = slot;
      this.aimHint('ally', t('skill.aimAlly'));
      return;
    }
    if (!fromButton && this.mouse.inside) {
      const g = this.groundAt(this.mouse.x, this.mouse.y);
      if (g) {
        const sp = worldToSim(g);
        this.castAt(slot, sp.x, sp.y, sk.targeting === 'guard' ? this.pickHero(this.mouse.x, this.mouse.y) : this.pickEnemy(this.mouse.x, this.mouse.y));
        return;
      }
    }
    this.aiming = this.aiming === slot ? -1 : slot;
    if (this.aiming >= 0) {
      this.overloading = false;
      if (sk.targeting === 'guard') this.aimHint('aim', t('skill.aimGuard'));
      else this.aimHint('aim', t('skill.aim'));
    }
  }

  /** What an overload can blow up now: its own turrets, and ready clockwork ones within the turret's reach. */
  private overloadTargets(): { x: number; y: number }[] {
    const me = this.me();
    const s = this.o.state;
    if (!me) return [];
    const out: { x: number; y: number }[] = s.turrets.filter((u) => u.owner === me.id && !u.portable).map((u) => ({ x: u.x, y: u.y }));
    for (const st of s.structures) {
      if (st.type !== 'clockwork' || st.destroyed || st.cd > WORKSHOP.overloadRest - 12) continue;
      const c = hexToWorldFixed(st.q, st.r);
      if (Math.hypot(c.x - me.x, c.y - me.y) <= SKILLS.turret.castRange) out.push(c);
    }
    return out;
  }

  /** The overload target nearest a point, within `r`. */
  private overloadPick(x: number, y: number, r: number): { x: number; y: number } | null {
    let best: { x: number; y: number } | null = null;
    let bd = r;
    for (const c of this.overloadTargets()) {
      const d = Math.hypot(c.x - x, c.y - y);
      if (d <= bd) {
        bd = d;
        best = c;
      }
    }
    return best;
  }

  /**
   * Overload button or F: with a single turret out it blows up at once; with F and the mouse over the board,
   * the one nearest the cursor; otherwise pick one with a tap.
   */
  private useOverload(fromButton: boolean): void {
    const me = this.me();
    if (!me || me.downTicks > 0 || (me.overloadCd ?? 0) > 0) return;
    const targets = this.overloadTargets();
    if (targets.length === 0) {
      this.overloading = false;
      this.toast(t('skill.overloadNone'), 2200);
      return;
    }
    if (this.overloading) {
      this.overloading = false;
      return;
    }
    let target = targets.length === 1 ? targets[0] : null;
    if (!target && !fromButton && this.mouse.inside) {
      const g = this.groundAt(this.mouse.x, this.mouse.y);
      if (g) {
        const sp = worldToSim(g);
        target = this.overloadPick(sp.x, sp.y, Infinity);
      }
    }
    if (target) {
      this.send({ t: 'overload', x: target.x, y: target.y });
      return;
    }
    this.aiming = -1;
    this.placingTurret = false;
    this.overloading = true;
    this.aimHint('overload', t('skill.aimOverload'));
  }

  /** The slot of this hero's skill that targets an ally (the cleric's mend, the paladin's aegis), or -1. */
  private allySlot(): number {
    const me = this.me();
    return me ? kitOf(me).findIndex((id) => SKILLS[id].targeting === 'ally' || SKILLS[id].targeting === 'guard') : -1;
  }

  /** Touching a name in the party list: heal that hero now, or say when the heal is back. */
  private healAlly(id: number): void {
    const me = this.me();
    const slot = this.allySlot();
    if (!me || slot < 0 || me.downTicks > 0) return;
    const target = this.o.state.heroes.find((x) => x.id === id);
    if (!target) return;
    if (target.downTicks > 0) {
      this.toast(t('skill.allyDown'));
      return;
    }
    const cd = me.skillCd[slot] as number;
    if (cd > 0) {
      this.toast(t('skill.allyWait', { n: Math.ceil(cd / 12) }));
      return;
    }
    this.aiming = -1;
    this.castAt(slot as 0 | 1 | 2, target.x, target.y, id);
    const p = this.view.positions.get(id);
    if (p) this.view.vfx.ring(p.clone().setY(p.y + 0.05), 0.2, 0.7, '#bff5d8', 6, 1);
  }

  /** The standing hero drawn nearest to a screen point (the caster too), 0 if none is close. */
  private pickHero(x: number, y: number): number {
    let best = 0;
    let bd = (isTouch() ? 56 : 40) ** 2;
    const v = new THREE.Vector3();
    for (const hero of this.o.state.heroes) {
      if (hero.downTicks > 0) continue;
      const p = this.view.positions.get(hero.id);
      if (!p) continue;
      v.copy(p).setY(p.y + 0.9).project(this.cam.camera);
      const d = ((v.x * 0.5 + 0.5) * viewW() - x) ** 2 + ((-v.y * 0.5 + 0.5) * viewH() - y) ** 2;
      if (d < bd) {
        bd = d;
        best = hero.id;
      }
    }
    return best;
  }

  /** Casts at a spot, or says why it wouldn't go (the sim drops such a cast without a word) and sends nothing. */
  private castAt(slot: 0 | 1 | 2, x: number, y: number, id: number): ReturnType<typeof castBlocked> {
    const me = this.me();
    const why = me ? castBlocked(this.o.state, me, slot, x, y) : null;
    if (why) {
      this.toast(t(`skill.no.${why}`), 2200);
      return why;
    }
    this.send({ t: 'skill', slot, x, y, ...(id ? { id } : {}) });
    return null;
  }

  // ---------- building (practice sandbox) ----------

  /** Terraced land: a drag with the wall, the stakes or the eraser paints every cell it crosses. */
  private paintAt(x: number, y: number): void {
    const g = this.groundAt(x, y);
    if (!g) return;
    const sp = worldToSim(g);
    const hx = worldToHex(sp.x, sp.y);
    const s = this.o.state;
    const id = cellId(s.map.radius, hx.q, hx.r);
    if (id < 0 || id === this.painted) return;
    const from = this.painted >= 0 ? cellHex(s.map.radius, this.painted) : hx;
    this.painted = id;
    for (const c of hexLine(from, hx)) {
      if (this.erasing) this.eraseCell(c.q, c.r);
      else if (this.building) {
        const p = hexToWorldFixed(c.q, c.r);
        if (this.placeCheck(this.building, p.x, p.y).ok) this.send({ t: 'place', s: this.building, q: c.q, r: c.r });
      }
    }
  }

  private eraseCell(q: number, r: number): void {
    const s = this.o.state;
    if (structureAtCell(s, cellId(s.map.radius, q, r))) this.send({ t: 'sell', q, r });
  }

  private toggleEraser(card: HTMLElement): void {
    this.erasing = !this.erasing;
    this.building = null;
    card.classList.toggle('active', this.erasing);
    if (this.erasing) this.toast(t('land.eraseHint'));
  }

  private cardDown(e: PointerEvent, type: StructureType, card: HTMLElement): void {
    e.preventDefault();
    e.stopPropagation();
    audio.unlock();
    const purse = this.o.state.purse;
    const cost = STRUCTURES[type].cost;
    if (purse && (purse.wood < cost.wood || purse.stone < cost.stone || purse.crystal < cost.crystal)) {
      this.toast(t('hud.notEnough'));
      return;
    }
    if (this.erasing) {
      this.erasing = false;
      this.el.querySelector('[data-test="erase"]')?.classList.remove('active');
    }
    const img = card.querySelector('img');
    const ghost = h('div', { style: `position:fixed;left:0;top:0;width:60px;height:60px;pointer-events:none;z-index:40;opacity:.9;transform:translate(${e.clientX - 30}px,${e.clientY - 70}px)` }, img ? (img.cloneNode() as HTMLElement) : '');
    this.el.append(ghost);
    this.dragCard = { type, pointerId: e.pointerId, ghost };
    this.building = type;
    this.mouse.x = e.clientX;
    this.mouse.y = e.clientY;
  }

  private finishCardDrag(e: PointerEvent): void {
    const d = this.dragCard;
    if (!d) return;
    d.ghost.remove();
    this.dragCard = null;
    const under = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
    const overDock = under?.closest?.('.dock');
    const g = this.groundAt(e.clientX, e.clientY);
    const card = this.cards.find((c) => c.type === d.type)?.el;
    const rect = card?.getBoundingClientRect();
    const tapped = rect && e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom;
    if (tapped) {
      // A tap on the card keeps build mode on: next tap on the board places it.
      this.toast(isTouch() ? t('hud.tapToPlace') : t('hud.dragToPlace'));
      return;
    }
    if (!overDock && g) {
      const sp = worldToSim(g);
      this.tryPlace(d.type, sp.x, sp.y);
    }
    this.building = null;
  }

  private placeCheck(type: StructureType, x: number, y: number): { q: number; r: number; ok: boolean } {
    const hx = worldToHex(x, y);
    const s = this.o.state;
    const cell = cellAtWorld(s, x, y);
    const purse = s.purse;
    const cost = STRUCTURES[type].cost;
    const afford = !!purse && purse.wood >= cost.wood && purse.stone >= cost.stone && purse.crystal >= cost.crystal;
    const ok = afford && cell >= 0 && isBuildableTerrain(terrainAt(s, hx.q, hx.r)) && !structureAtCell(s, cell);
    return { q: hx.q, r: hx.r, ok };
  }

  private tryPlace(type: StructureType, x: number, y: number): void {
    const c = this.placeCheck(type, x, y);
    if (!c.ok) {
      this.toast(t('hud.blocked'));
      return;
    }
    this.send({ t: 'place', s: type, q: c.q, r: c.r });
  }

  /** A turret's reach on the ground: under the cursor while aiming one, then a moment where it landed. */
  private turretReachUntil = 0;

  private aimingTurret(): boolean {
    const me = this.me();
    return this.placingTurret || (!!me && this.aiming >= 0 && kitOf(me)[this.aiming as 0 | 1 | 2] === 'turret');
  }

  private showTurretReach(x: number, y: number): void {
    this.view.showRange(x, y, SKILLS.turret.radius, '#eceef2', 0.45);
    this.turretReachUntil = performance.now() + 2500;
  }

  private updateHover(): void {
    if (this.overloading && this.mouse.inside) {
      // Picking a turret to overload: the blast's red ring on the one a tap here would take.
      const g = this.groundAt(this.mouse.x, this.mouse.y);
      const own = g ? this.overloadPick(worldToSim(g).x, worldToSim(g).y, isTouch() ? 1600 : 1000) : null;
      if (own) {
        this.view.showRange(own.x, own.y, ARTIFICER.overload.radius, '#ff4f3a', 0.55);
        this.turretReachUntil = performance.now() + 2500;
      }
    } else if (this.aimingTurret() && this.mouse.inside) {
      const g = this.groundAt(this.mouse.x, this.mouse.y);
      if (g) {
        const sp = worldToSim(g);
        this.showTurretReach(sp.x, sp.y);
      }
    } else if (this.turretReachUntil && performance.now() > this.turretReachUntil) {
      this.turretReachUntil = 0;
      this.view.hideRange();
    }
    if (!this.building || (!this.mouse.inside && !this.dragCard)) {
      this.view.setPreview(null, null, false);
      this.view.setHover(null, false);
      return;
    }
    const g = this.groundAt(this.mouse.x, this.mouse.y);
    if (!g) return;
    const sp = worldToSim(g);
    const c = this.placeCheck(this.building, sp.x, sp.y);
    this.hoverCell = { q: c.q, r: c.r };
    this.view.setHover(this.hoverCell, c.ok);
    this.view.setPreview(this.building, this.hoverCell, c.ok, STRUCTURES[this.building].range);
  }

  destroy(): void {
    this.loop.stop();
    this.meter.send();
    this.chat?.dispose();
    this.signals?.dispose();
    if (this.dg instanceof FurrowsHud) this.dg.dispose();
    window.clearInterval(this.socialTimer);
    this.dropAllies();
    this.o.net?.close();
    for (const d of this.disposers) d();
    this.tipEl?.remove();
    this.coach?.destroy();
    this.marks.dispose();
    // The post-processing's full-screen targets too: left behind, each fight leaked hundreds of MB of GPU
    // memory on a tablet, until the browser dropped the WebGL context.
    this.post.dispose();
    this.view.dispose();
    this.el.remove();
  }
}

export type { SkillId };
